import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Send,
  ChevronDown,
  ChevronUp,
  Upload,
  Sparkles,
  RefreshCw,
  ArrowRight,
  MessageSquare,
  Users,
  FileText,
  BarChart2,
  Settings,
  Zap,
  ShoppingBag,
  CreditCard,
  Link2,
  Bot,
  Globe,
  Package,
  LayoutDashboard,
  Bell,
  Clock,
  HelpCircle,
  Phone,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  ShoppingCart,
  Tag,
  Layers,
  Share2,
  ImageIcon,
  TrendingUp,
  MessageCircle,
  Eye,
  BarChart3,
  UserCog,
  Megaphone,
  User,
  Mic,
  MicOff,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGenUI } from './GenUIContext';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { apiClient } from '@/lib/api/client';
import { ApiError } from '@/lib/api';
import { transcribeAudio } from '@/lib/api/services/genui';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { useLocation, useNavigate } from 'react-router-dom';
import { woocommerceApi } from '@/lib/api/services/woocommerce';
import { razorpayApi } from '@/lib/api/services/razorpay';
import { WhatsAppEmbeddedSignup } from '@/components/WhatsAppEmbeddedSignup';
import { useQueryClient } from '@tanstack/react-query';
import { GenUIComponentRenderer } from './GenUIComponentRenderer';

interface GenUIChatboxProps {
  mode?: 'floating' | 'page';
}

// ─── Types ───────────────────────────────────────────────────────────────────
interface DisplayMessage {
  id: string;
  role: 'ai' | 'user';
  content: string;
  timestamp: Date;
  showFileUpload?: boolean;
  isError?: boolean;
  toolCalled?: string | null;
  toolResult?: any;
  showCard?: boolean;
  extraCards?: Array<{ toolCalled: string; toolResult: any }>;
  /** AI-generated quick-reply chips (from backend suggestedActions) */
  suggestedActions?: Array<{ label: string; prompt: string }>;
  /** Soft agent suggestion — show a compact "Switch to X" chip alongside the reply */
  suggestAgent?: { id: string; name: string; label: string; color: string } | null;
  /** Legacy confirmation fields — kept for session restore compat */
  isConfirmationRequest?: boolean;
  confirmationPrimaryLabel?: string;
  confirmationPrimaryPrompt?: string;
  confirmationSecondaryLabel?: string;
  confirmationSecondaryPrompt?: string;
}

interface HistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

function needsFileUpload(text: string): boolean {
  return /upload.*button|upload.*csv|csv.*upload|upload.*file|file.*upload/i.test(text);
}


function shouldStayInAutomationBuilder(text: string): boolean {
  const value = text.trim().toLowerCase();
  if (!value) return false;

  return /\b(automation|flow|builder|canvas|node|block|trigger|starting step|start step|message node|list node|quick repl(?:y|ies)|button|connect|branch|condition|publish flow|save flow)\b/.test(value);
}


function isTemplateCategoryPromptText(text: string): boolean {
  const lowered = text.trim().toLowerCase();
  return /\b(template categories|pick one|choose one|select one|what type of template|which type of template|what category should it be|which category|marketing, utility,? or authentication)\b/.test(lowered);
}

function shouldRenderTemplateCategoryFallback(text: string, toolCalled: string | null): boolean {
  if (!isTemplateCategoryPromptText(text)) return false;
  // Show when AI explicitly called the tool OR responded with the category question without a tool call
  return toolCalled === 'get_template_categories' || toolCalled === null;
}

function getTemplateCategoryFallbackResult() {
  return {
    success: true,
    categories: [
      { id: 'MARKETING', name: 'Marketing', description: 'Promotions, offers, and brand updates.' },
      { id: 'UTILITY', name: 'Utility', description: 'Order updates, alerts, and account info.' },
      { id: 'AUTHENTICATION', name: 'Authentication', description: 'One-time passwords (OTP).' },
    ],
  };
}

// ─── Component ───────────────────────────────────────────────────────────────
// ── Map: tool name → query keys to invalidate on success ────────────────────
const TOOL_INVALIDATION: Record<string, string[]> = {
  create_template: ['templates'],
  sync_templates: ['templates'],
  create_campaign: ['campaigns'],
  campaigns_create: ['campaigns'],
  control_campaign: ['campaigns'],
  get_campaign_analytics: ['campaign-analytics'],
  create_contact: ['contacts'],
  update_contact: ['contacts'],
  create_tag: ['tags'],
  assign_tags_to_contacts: ['contacts', 'tags'],
  update_conversation: ['conversations'],
  create_quick_reply: ['quickReplies'],
  create_automation_flow: ['automation-flows'],
  automation_create_flow: ['automation-flows'],
  automation_list_flows: ['automation-flows'],
  automation_toggle_flow: ['automation-flows'],
  automation_update_flow: ['automation-flows', 'automation-flow'],
  automation_delete_flow: ['automation-flows'],
  automation_duplicate_flow: ['automation-flows'],
  add_flow_node: ['automation-flow'],
  save_current_flow: ['automation-flows', 'automation-flow'],
};

const PAGE_STARTER_GROUPS = [
  {
    title: 'Popular tasks',
    items: [
      { label: 'Create a template', prompt: 'Create a template', icon: FileText },
      { label: 'Launch a campaign', prompt: 'Launch a campaign', icon: Zap },
      { label: 'Show contacts', prompt: 'Show contacts', icon: Users },
      { label: 'Open inbox', prompt: 'Open inbox', icon: MessageSquare },
    ],
  },
  {
    title: 'Analytics and commerce',
    items: [
      { label: 'View analytics', prompt: 'View analytics', icon: BarChart2 },
      { label: 'Store stats', prompt: 'Show my commerce stats', icon: ShoppingCart },
      { label: 'List products', prompt: 'Show my products', icon: Layers },
      { label: 'Check orders', prompt: 'Show my recent orders', icon: Package },
    ],
  },
];

const PAGE_CAPABILITIES = [
  { label: 'Campaigns', icon: Zap },
  { label: 'Contacts', icon: Users },
  { label: 'Analytics', icon: BarChart2 },
  { label: 'Commerce', icon: ShoppingCart },
];

export const GenUIChatbox: React.FC<GenUIChatboxProps> = ({ mode = 'floating' }) => {
  const { isOpen, setIsOpen, handleAction } = useGenUI();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedAccountId } = useBusinessAccountContext();
  const queryClient = useQueryClient();

  const [display, setDisplay] = useState<DisplayMessage[]>([]);
  const [history, setHistory] = useState<HistoryMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingLabel, setTypingLabel] = useState('Aria is thinking...');
  const [isMinimized, setIsMinimized] = useState(false);
  const [pendingCampaignId, setPendingCampaignId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initialized = useRef(false);
  const isCallingRef = useRef(false);
  const pendingNavigationTimeoutsRef = useRef<number[]>([]);
  const lastFlowUrlRef = useRef<string | null>(null);
  // Live canvas state from FlowBuilder (updated via CustomEvent)
  const canvasNodesRef = useRef<Array<{ nodeId: string; nodeType: string; label: string; config?: any }>>([]);
  const canvasEdgesRef = useRef<Array<{ source: string; target: string; label: string }>>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const voiceLangRef = useRef<string | null>(null);
  const isPageMode = mode === 'page';
  const shouldRender = isPageMode || isOpen;
  const activeBizId = selectedAccountId || (user as any)?.business_account_id || 'default';
  const storageKey = isPageMode
    ? `genui-chat-page-state-v1-${activeBizId}`
    : `genui-chat-floating-state-v1-${activeBizId}`;
  const handoffStorageKey = 'genui-chat-page-handoff-v1';
  const lastIntentStorageKey = 'genui-last-intent-v1';

  useEffect(() => {
    // Reset state when switching business accounts, then load that account's history
    setDisplay([]);
    setHistory([]);
    setPendingCampaignId(null);
    initialized.current = false;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed.display) && Array.isArray(parsed.history)) {
        setDisplay(parsed.display.map((msg: any) => ({
          ...msg,
          timestamp: msg?.timestamp ? new Date(msg.timestamp) : new Date(),
        })));
        setHistory(parsed.history);
        setPendingCampaignId(parsed.pendingCampaignId || null);
        initialized.current = parsed.initialized === true;
      }
    } catch {
      // Ignore session restore issues
    }
  }, [storageKey]);

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        initialized: initialized.current,
        display,
        history,
        pendingCampaignId,
      }));
    } catch {
      // Ignore session persistence issues
    }
  }, [display, history, pendingCampaignId, storageKey]);

  useEffect(() => {
    const handleCanvasState = (e: Event) => {
      canvasNodesRef.current = (e as CustomEvent).detail?.nodes ?? [];
      canvasEdgesRef.current = (e as CustomEvent).detail?.edges ?? [];
    };
    window.addEventListener('aria:canvas-state', handleCanvasState);
    return () => window.removeEventListener('aria:canvas-state', handleCanvasState);
  }, []);

  const clearPendingNavigationTimeouts = useCallback(() => {
    pendingNavigationTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    pendingNavigationTimeoutsRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearPendingNavigationTimeouts();
    };
  }, [clearPendingNavigationTimeouts]);

  // ── Scroll to bottom ────────────────────────────────────────────────────────
  useEffect(() => {
    if (shouldRender && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [display, shouldRender, isMinimized]);

  // ── Auto-focus textarea when chat opens ──────────────────────────────────────
  useEffect(() => {
    if (shouldRender && !isMinimized) {
      setTimeout(() => textareaRef.current?.focus(), 120);
    }
  }, [shouldRender, isMinimized]);

  // ── Welcome message on first open ───────────────────────────────────────────
  useEffect(() => {
    if (shouldRender && !initialized.current) {
      initialized.current = true;
      const name = (user as any)?.full_name?.split(' ')[0] || 'there';
      setDisplay([{
        id: uid(),
        role: 'ai',
        timestamp: new Date(),
        content: `Hey ${name}! 👋 I'm **Aria**, your PhotonX AI assistant.\n\nI can help you with **templates**, **campaigns**, **contacts**, **inbox**, **automation flows**, **analytics**, **orders**, and more.\n\nWhat would you like to do today?`,
      }]);
    }
  }, [shouldRender, user]);

  // ── Call the backend AI agent ────────────────────────────────────────────────
  const callAgent = useCallback(async (updatedHistory: HistoryMessage[]) => {
    const token = localStorage.getItem('auth_token');
    const bizId = selectedAccountId || (user as any)?.business_account_id;
    const res = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/v1/genui/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages: updatedHistory, businessAccountId: bizId, canvasNodes: canvasNodesRef.current, canvasEdges: canvasEdgesRef.current, voiceLanguage: voiceLangRef.current }),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult(err, 'Request failed');
    }
    return res.json() as Promise<{
      success: boolean;
      reply: string;
      toolCalled: string | null;
      toolResult: any;
      showCard?: boolean;
      extraCards?: Array<{ toolCalled: string; toolResult: any }>;
      suggestedActions?: Array<{ label: string; prompt: string }>;
    }>;
  }, [user, selectedAccountId]);

  // ── Unified send function with ref guard ────────────────────────────────────
  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isCallingRef.current) return;
    const isAutomationIntent = shouldStayInAutomationBuilder(trimmed);

    clearPendingNavigationTimeouts();

    try {
      sessionStorage.setItem(lastIntentStorageKey, isAutomationIntent ? 'automation' : 'general');
    } catch {
      // Ignore intent persistence issues
    }



    isCallingRef.current = true;

    setDisplay(prev => [...prev, { id: uid(), role: 'user', content: trimmed, timestamp: new Date() }]);
    const newHistory: HistoryMessage[] = [...history, { role: 'user', content: trimmed }];
    setHistory(newHistory);

    // Contextual typing label based on user intent
    const lc = trimmed.toLowerCase();
    let label = 'Aria is thinking...';
    if (/create|build|make|generate|set up|setup/.test(lc) && /flow|automation/.test(lc)) label = 'Building your automation flow...';
    else if (/create|build|make|generate|set up/.test(lc) && /campaign/.test(lc)) label = 'Setting up your campaign...';
    else if (/analytic|report|stat|metric|insight/.test(lc)) label = 'Fetching analytics data...';
    else if (/contact|crm/.test(lc) && /show|find|search|list|get/.test(lc)) label = 'Searching contacts...';
    else if (/template/.test(lc)) label = 'Loading templates...';
    else if (/connect|integrat|woocommerce|shopify|razorpay/.test(lc)) label = 'Checking integration status...';
    else if (/schedule|appointment|booking/.test(lc)) label = 'Checking schedule...';
    else if (/order|product|commerce/.test(lc)) label = 'Fetching store data...';
    else if (/send.*message|message.*send/.test(lc)) label = 'Preparing message...';
    else if (/inbox|conversation/.test(lc)) label = 'Loading conversations...';
    else if (/dashboard|overview/.test(lc)) label = 'Loading dashboard...';
    else if (/yes|ok|sure|go ahead|create it|do it|confirm/.test(lc)) label = 'On it, working on it...';
    setTypingLabel(label);
    setIsTyping(true);

    try {
      const data = await callAgent(newHistory);
      const aiReply = data.reply || "I'm here! Could you tell me more?";
      const shouldShowTemplateCategories = shouldRenderTemplateCategoryFallback(aiReply, data.toolCalled);
      const resolvedToolCalled = shouldShowTemplateCategories
        ? 'get_template_categories'
        : data.toolCalled;
      const resolvedToolResult = shouldShowTemplateCategories
        ? (data.toolResult?.success ? data.toolResult : getTemplateCategoryFallbackResult())
        : data.toolResult;
      let resolvedShowCard = shouldShowTemplateCategories ? true : (data.showCard ?? true);

      if ((resolvedToolCalled === 'templates_create' || resolvedToolCalled === 'campaigns_create') && resolvedToolResult?.success) {
        // Show TemplateFormCard/CampaignDetailCard inline — no redirection, no modal, no sessionStorage
        resolvedShowCard = true;
      }

      setHistory(prev => [...prev, { role: 'assistant', content: aiReply }]);
      if ((resolvedToolCalled === 'create_campaign' || resolvedToolCalled === 'campaigns_create') && resolvedToolResult?.requiresCsvUpload) {
        setPendingCampaignId(resolvedToolResult?.campaignId || null);
      }
      if (['create_automation_flow', 'automation_create_flow'].includes(resolvedToolCalled ?? '') && resolvedToolResult?.navigateTo) {
        lastFlowUrlRef.current = resolvedToolResult.navigateTo;
      }
      if (
        ['add_flow_node', 'update_flow_node', 'connect_flow_nodes'].includes(resolvedToolCalled ?? '') &&
        resolvedToolResult?.success &&
        lastFlowUrlRef.current &&
        !location.pathname.startsWith('/home/automation/flows/')
      ) {
        navigate(lastFlowUrlRef.current);
      }
      // Auto-refresh relevant pages when Aria creates/modifies data
      if (resolvedToolCalled && resolvedToolResult?.success && TOOL_INVALIDATION[resolvedToolCalled]) {
        TOOL_INVALIDATION[resolvedToolCalled].forEach(key => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      }
      setDisplay(prev => [...prev, {
        id: uid(), role: 'ai', content: aiReply, timestamp: new Date(),
        toolCalled: resolvedToolCalled,
        toolResult: resolvedToolResult,
        showCard: resolvedShowCard,
        extraCards: data.extraCards,
        showFileUpload: needsFileUpload(aiReply) || ((resolvedToolCalled === 'create_campaign' || resolvedToolCalled === 'campaigns_create') && resolvedToolResult?.requiresCsvUpload),
        suggestedActions: (data as any).suggestedActions || undefined,
        suggestAgent: (data as any).suggestAgent || null,
      }]);
    } catch {
      setDisplay(prev => [...prev, {
        id: uid(), role: 'ai',
        content: "I ran into a small issue on my end. Could you try again?",
        timestamp: new Date(), isError: true,
      }]);
    } finally {
      setIsTyping(false);
      isCallingRef.current = false;
      voiceLangRef.current = null; // clear after each message — only applies to voice turns
    }
  }, [history, callAgent, clearPendingNavigationTimeouts, navigate, isPageMode, location.pathname, setIsOpen, handoffStorageKey, lastIntentStorageKey]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    sendMessage(text);
  }, [input, sendMessage]);

  // ── Voice input ───────────────────────────────────────────────────────────────
  const handleMicClick = useCallback(async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Pick the best supported mime type; fall back to browser default
      const mimeType = ['audio/webm', 'audio/mp4', 'audio/ogg'].find(t =>
        MediaRecorder.isTypeSupported(t)
      ) || '';

      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Request data every 250 ms so onstop gets all chunks reliably
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setIsRecording(false);
        setIsTranscribing(true);
        try {
          const recordedMime = mediaRecorder.mimeType || mimeType || 'audio/webm';
          const blob = new Blob(audioChunksRef.current, { type: recordedMime });
          const { transcript, detectedLanguage } = await transcribeAudio(blob);
          voiceLangRef.current = detectedLanguage; // e.g. "te", "hi", "ta"
          if (transcript?.trim()) {
            setInput(transcript.trim());
            setTimeout(() => textareaRef.current?.focus(), 50);
          }
        } catch (err) {
          console.error('[Voice] Transcription failed:', err);
          showApiError(err, { fallback: { title: 'Voice transcription failed' } });
          setInput('');
          setTimeout(() => textareaRef.current?.focus(), 50);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start(250); // collect chunks every 250 ms
      setIsRecording(true);
    } catch (err) {
      console.error('[Voice] Mic access denied:', err);
      toast({
        title: 'Microphone access denied',
        description: 'Please allow microphone access in your browser settings.',
        variant: 'destructive',
      });
    }
  }, [isRecording]);

  // ── File upload ──────────────────────────────────────────────────────────────
  const handleFileSelect = useCallback(async (file: File) => {
    const sizeKB = (file.size / 1024).toFixed(1);
    setDisplay(prev => [...prev, { id: uid(), role: 'user', content: `📎 ${file.name} (${sizeKB} KB)`, timestamp: new Date() }]);
    const bizId = selectedAccountId || (user as any)?.business_account_id;
    setIsTyping(true);
    try {
      if (pendingCampaignId) {
        const form = new FormData();
        form.append('contactsFile', file);
        if (bizId) form.append('businessAccountId', bizId);
        const res = await apiClient.post(`/campaigns/${pendingCampaignId}/recipients/import`, form);
        if (res.error) throw ApiError.fromResult(res);
        setPendingCampaignId(null);
        const aiReply = `Contacts from **"${file.name}"** added to the campaign! 🎉 Anything else I can help with?`;
        setHistory(prev => [...prev, { role: 'assistant', content: aiReply }]);
        setDisplay(prev => [...prev, { id: uid(), role: 'ai', content: aiReply, timestamp: new Date() }]);
      } else {
        const form = new FormData();
        form.append('file', file);
        if (bizId) form.append('businessAccountId', bizId);
        const res = await apiClient.post('/contacts/import', form);
        if (res.error) throw ApiError.fromResult(res);
        const aiReply = `Contacts from **"${file.name}"** imported successfully! 👥 They'll appear in your CRM shortly.\n\nIs there anything else I can help you with?`;
        setHistory(prev => [...prev, { role: 'assistant', content: aiReply }]);
        setDisplay(prev => [...prev, { id: uid(), role: 'ai', content: aiReply, timestamp: new Date() }]);
      }
    } catch (err: any) {
      const errMsg = `There was a problem uploading "${file.name}": ${err?.message || 'Unknown error'}. Please check the file and try again.`;
      setHistory(prev => [...prev, { role: 'assistant', content: errMsg }]);
      setDisplay(prev => [...prev, { id: uid(), role: 'ai', content: errMsg, timestamp: new Date(), isError: true }]);
    } finally {
      setIsTyping(false);
    }
  }, [user, selectedAccountId, pendingCampaignId]);

  // ── Reset ────────────────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    initialized.current = false;
    isCallingRef.current = false;
    setDisplay([]);
    setHistory([]);
    setInput('');
    setIsTyping(false);
    setPendingCampaignId(null);
    sessionStorage.removeItem(storageKey);
    setTimeout(() => {
      initialized.current = true;
      const name = (user as any)?.full_name?.split(' ')[0] || 'there';
      setDisplay([{
        id: uid(), role: 'ai', timestamp: new Date(),
        content: `Hey ${name}! 👋 I'm **Aria**, your PhotonX AI assistant.\n\nWhat would you like to do today?`,
      }]);
    }, 40);
  }, [user]);

  useEffect(() => {
    if (!isPageMode || !shouldRender || isCallingRef.current) return;

    try {
      const handoffPrompt = sessionStorage.getItem(handoffStorageKey);
      if (!handoffPrompt) return;
      sessionStorage.removeItem(handoffStorageKey);
      setTimeout(() => {
        sendMessage(handoffPrompt);
      }, 80);
    } catch {
      // Ignore handoff restore issues
    }
  }, [handoffStorageKey, isPageMode, sendMessage, shouldRender]);

  if (!shouldRender) {
    const isAutomationPage = location.pathname.startsWith('/home/automation') || location.pathname.startsWith('/home/flows');
    if (!isPageMode && !isAutomationPage) {
      return (
        <AnimatePresence>
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-24 z-[101] flex h-14 w-14 items-center justify-center rounded-full bg-[#22C55E] text-white shadow-[0_8px_30px_rgba(34,197,94,0.35)] transition-transform hover:scale-110 hover:bg-[#16A34A] active:scale-95 border-2 border-white/20"
          >
            <Bot size={26} />
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 border-2 border-white text-[9px] text-white"></span>
          </motion.button>
        </AnimatePresence>
      );
    }
    return null;
  }

  const lastMsg = display[display.length - 1];
  const showUpload = !isTyping && lastMsg?.role === 'ai' && lastMsg?.showFileUpload;
  const heroName = (user as any)?.full_name?.split(' ')[0] || 'there';
  const showPageEmptyState = isPageMode && display.length <= 1 && !isTyping;
  const pageLeadMessage = display[0]?.content || `Hey ${heroName}! I'm Aria, your PhotonX AI assistant.`;

  return (
    <AnimatePresence>
      <motion.div
        initial={isPageMode ? { opacity: 0, y: 16 } : { opacity: 0, scale: 0.92, y: 28 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={isPageMode ? { opacity: 0, y: 16 } : { opacity: 0, scale: 0.92, y: 28 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          "flex flex-col overflow-hidden pointer-events-auto",
          isPageMode
            ? "h-full w-full rounded-[28px] border border-[#E2E8F0] bg-[#F8F9FB] shadow-[0_24px_80px_-8px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)]"
            : [
              "fixed bottom-6 right-6 z-[101] rounded-3xl",
              "shadow-[0_24px_80px_-8px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.06)]",
              isMinimized
                ? "h-14 w-80 bg-[#0F172A]"
                : "h-[650px] max-h-[88vh] w-[480px] max-w-[calc(100vw-1.5rem)] min-w-[340px] bg-[#F8F9FB]",
            ]
        )}
      >
        {/* ── Header ── */}
        {isPageMode && (
          <div className="flex shrink-0 items-center justify-between border-b border-[#E2E8F0] bg-white px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 text-[#22C55E]">
                <Sparkles size={16} />
              </div>
              <h3 className="text-[17px] font-bold text-slate-900 tracking-tight">GenUI Chat</h3>
            </div>
            <Button variant="ghost" size="icon" title="Reset conversation"
              className="h-9 w-9 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              onClick={handleReset}>
              <RefreshCw size={18} />
            </Button>
          </div>
        )}

        {!isPageMode && (
          <div
            className={cn(
              "flex shrink-0 items-center justify-between px-4 py-3 text-white",
              isMinimized && "h-full"
            )}
            style={{ backgroundImage: 'linear-gradient(135deg, #1e0a4e 0%, #3b0764 45%, #6d28d9 100%)' }}
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {!isMinimized && (
                <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                  <Sparkles size={16} className="text-white" />
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#3b0764] bg-emerald-400" />
                </div>
              )}
              <div className="min-w-0">
                <h3 className={cn("font-bold tracking-tight text-white", isMinimized ? "text-base" : "text-[17px] leading-tight")}>
                  Aria
                </h3>
              </div>
            </div>

            <div className="ml-2 flex shrink-0 items-center gap-0.5">
              {!isMinimized && (
                <>
                  <span className="mr-2 flex items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[9.5px] font-semibold tracking-widest text-white/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    LIVE
                  </span>
                  <Button variant="ghost" size="icon" title="New conversation"
                    className="h-7 w-7 text-white/70 hover:bg-white/15 hover:text-white"
                    onClick={handleReset}>
                    <RefreshCw size={12} />
                  </Button>
                </>
              )}
              <Button variant="ghost" size="icon"
                className="h-7 w-7 text-white/70 hover:bg-white/15 hover:text-white"
                onClick={e => { e.stopPropagation(); setIsMinimized(!isMinimized); }}>
                {isMinimized ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </Button>

            </div>
          </div>
        )}

        {!isMinimized && (
          <>
            <div className="flex flex-1 min-h-0 flex-col">
              {/* ── Messages ── */}
              <div
                className={cn(
                  "flex-1 space-y-5 overflow-y-auto px-4 py-5",
                  isPageMode ? "px-10 py-8" : ""
                )}
                style={{ background: isPageMode ? 'linear-gradient(180deg,#f8f5ff 0%,#ffffff 45%,#fbf9ff 100%)' : 'linear-gradient(180deg,#f3eeff 0%,#faf8ff 30%,#ffffff 70%,#f7f4ff 100%)' }}
              >
                {showPageEmptyState ? (
                  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center pb-4">
                    <div className="mx-auto max-w-3xl text-center">
                      <h2 className="text-[42px] font-semibold tracking-tight text-slate-900">Use chat to run work across the platform.</h2>
                      <div className="mt-5 text-[16px] leading-8 text-slate-600">
                        {renderContent(pageLeadMessage)}
                      </div>
                      <div className="mt-6 flex flex-wrap justify-center gap-2">
                        {PAGE_CAPABILITIES.map(({ label, icon: Icon }) => (
                          <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white px-3 py-1.5 text-[11px] font-medium text-[#64748B]">
                            <Icon className="h-3.5 w-3.5" />
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      {PAGE_STARTER_GROUPS.flatMap((group) => group.items).slice(0, 8).map(({ label, prompt, icon: Icon }) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => sendMessage(prompt)}
                          disabled={isTyping}
                          className="flex items-start gap-4 rounded-[28px] border border-[#E2E8F0] bg-white px-5 py-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-green-200 hover:shadow-[0_8px_24px_-8px_rgba(34,197,94,0.15)] disabled:opacity-50"
                        >
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-[#22C55E]">
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-base font-semibold text-slate-900">{label}</p>
                            <p className="mt-1 text-sm leading-6 text-slate-500">{prompt}</p>
                          </div>
                        </button>
                      ))}
                    </div>

                  </div>
                ) : (
                  <>
                    <AnimatePresence initial={false}>
                      {display.map((msg, index) => {
                        const hasLaterUserReply = display.slice(index + 1).some((laterMsg) => laterMsg.role === 'user');
                        const shouldHideResolvedToolCard =
                          msg.toolCalled === 'get_template_categories' &&
                          (hasLaterUserReply || !isTemplateCategoryPromptText(msg.content));

                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 16, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                            className={cn(
                              "flex flex-col",
                              msg.role === 'user'
                                ? (isPageMode ? "items-end ml-24" : "items-end ml-10")
                                : (isPageMode ? "items-start mr-32" : "items-start mr-10")
                            )}
                          >
                            {/* Label row */}
                            <div className={cn(
                              "mb-1.5 flex items-center gap-1.5",
                              msg.role === 'user' && "flex-row-reverse"
                            )}>
                              {msg.role === 'ai' ? (
                                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-green-500 shadow-sm">
                                  <Sparkles size={12} className="text-white" />
                                </div>
                              ) : (
                                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 shadow-sm">
                                  <User size={12} className="text-white" />
                                </div>
                              )}
                              <span className="text-[11px] font-medium text-slate-500">
                                {msg.role === 'ai' ? 'Aria' : 'You'}
                              </span>
                            </div>

                            {/* Bubble */}
                            <div className={cn(
                              "rounded-2xl text-[13.5px] leading-relaxed",
                              msg.role === 'user'
                                ? "rounded-tr-[6px] px-4 py-3 bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-[0_4px_16px_rgba(15,23,42,0.2)]"
                                : msg.isError
                                  ? "rounded-tl-[6px] px-4 py-3 border border-red-200 bg-red-50 text-red-700 shadow-sm"
                                  : isRichContent(msg.content)
                                    ? "rounded-tl-[6px] border border-slate-200/60 bg-white text-slate-800 shadow-[0_4px_20px_rgba(0,0,0,0.07)] overflow-hidden"
                                    : "rounded-tl-[6px] border border-slate-200/60 border-l-2 border-l-emerald-400 bg-white text-slate-800 shadow-[0_4px_20px_rgba(0,0,0,0.07)] px-4 py-3"
                            )}>
                              {msg.role === 'user'
                                ? <p className="leading-relaxed">{msg.content}</p>
                                : renderContent(msg.content)
                              }
                            </div>

                            {/* Structured data card */}
                            {msg.role === 'ai' && msg.toolCalled && msg.toolResult?.success && (DATA_TOOLS.has(msg.toolCalled) || !!msg.toolResult?.componentHint) && msg.showCard !== false && !shouldHideResolvedToolCard && (
                              <div className="mt-2 w-full">
                                <ToolResultRenderer toolCalled={msg.toolCalled} toolResult={msg.toolResult} sendMessage={sendMessage} navigate={navigate} onAction={handleAction} toolCallId={msg.id} />
                              </div>
                            )}
                            {/* Extra cards (secondary tool results, e.g. template list alongside contacts) */}
                            {msg.role === 'ai' && msg.showCard !== false && !shouldHideResolvedToolCard && msg.extraCards?.filter((ec, index, cards) => {
                              if (!ec.toolResult?.success || (!DATA_TOOLS.has(ec.toolCalled) && !ec.toolResult?.componentHint)) return false;
                              const itemCount = ec.toolResult?.count ?? ec.toolResult?.total_count ?? ec.toolResult?.conversations?.length;
                              const key = `${ec.toolCalled}:${itemCount}:${(ec.toolResult?.matched_contact_name || '').toLowerCase()}`;
                              return cards.findIndex((candidate) => {
                                const candidateCount = candidate.toolResult?.count ?? candidate.toolResult?.total_count ?? candidate.toolResult?.conversations?.length;
                                const candidateKey = `${candidate.toolCalled}:${candidateCount}:${(candidate.toolResult?.matched_contact_name || '').toLowerCase()}`;
                                return candidateKey === key;
                              }) === index;
                            }).map((ec, i) => (
                              ec.toolResult?.success && (DATA_TOOLS.has(ec.toolCalled) || !!ec.toolResult?.componentHint) ? (
                                <div key={i} className="mt-2 w-full">
                                  <ToolResultRenderer toolCalled={ec.toolCalled} toolResult={ec.toolResult} sendMessage={sendMessage} navigate={navigate} onAction={handleAction} toolCallId={`${msg.id}-extra-${i}`} />
                                </div>
                              ) : null
                            ))}

                            {/* CSV upload zone */}
                            {msg.role === 'ai' && msg.showFileUpload && msg.id === display[display.length - 1]?.id && !isTyping && (
                              <div className="mt-3 w-full">
                                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); e.target.value = ''; }} />
                                <button type="button" onClick={() => fileInputRef.current?.click()}
                                  className="group w-full rounded-2xl border-2 border-dashed border-[#E2E8F0] bg-slate-50/80 px-4 py-4 text-center transition-all hover:border-green-300 hover:bg-green-50/40">
                                  <Upload className="mx-auto mb-2 h-5 w-5 text-slate-400 transition-transform group-hover:scale-110 group-hover:text-[#22C55E]" />
                                  <p className="text-[13px] font-semibold text-slate-600">Upload CSV or Excel</p>
                                  <p className="mt-0.5 text-[11px] text-slate-400">Columns: name, phone_number</p>
                                </button>
                              </div>
                            )}

                            {/* AI-driven quick-reply chips */}
                            {msg.role === 'ai' && msg.suggestedActions && msg.suggestedActions.length > 0 && msg.id === display[display.length - 1]?.id && !isTyping && (
                              <div className="mt-2.5 flex flex-wrap gap-1.5">
                                {msg.suggestedActions.map((action, i) => {
                                  const isDismiss = /^(no|cancel|not now|dismiss|skip|nevermind|never mind)/i.test(action.label);
                                  return (
                                    <button
                                      key={i}
                                      type="button"
                                      onClick={() => sendMessage(action.prompt)}
                                      className={cn(
                                        "rounded-full border px-3.5 py-1.5 text-[12px] font-medium transition-all duration-150 active:scale-95",
                                        isDismiss
                                          ? "border-slate-200 bg-white text-slate-400 hover:border-red-200 hover:text-red-400"
                                          : "border-violet-200/80 bg-white text-violet-600 shadow-[0_1px_4px_rgba(109,40,217,0.08)] hover:border-violet-400 hover:bg-violet-50 hover:shadow-[0_2px_8px_rgba(109,40,217,0.15)]"
                                      )}
                                    >
                                      {action.label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            <span className="mt-2 text-[10px] text-slate-400/70 select-none">
                              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </motion.div>
                        )
                      })}
                    </AnimatePresence>

                    {/* Typing indicator */}
                    {isTyping && (
                      <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className={cn("flex flex-col items-start", isPageMode ? "mr-32" : "mr-10")}
                      >
                        <div className="mb-1.5 flex items-center gap-1.5">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-green-500 shadow-sm">
                            <Sparkles size={12} className="text-white" />
                          </div>
                          <span className="text-[11px] font-medium text-slate-500">Aria</span>
                        </div>
                        <div className="rounded-2xl rounded-tl-[6px] border border-slate-200/60 bg-white px-4 py-3 shadow-[0_2px_14px_rgba(0,0,0,0.06)]">
                          <div className="flex items-end gap-0.5">
                            <span className="text-[12.5px] text-slate-500 italic">{typingLabel.replace(/\.+$/, '')}</span>
                            <span className="flex items-end gap-[2px] pb-[1px]">
                              <span className="animate-bounce inline-block text-[12.5px] text-slate-500 italic leading-none [animation-delay:-0.32s]">.</span>
                              <span className="animate-bounce inline-block text-[12.5px] text-slate-500 italic leading-none [animation-delay:-0.16s]">.</span>
                              <span className="animate-bounce inline-block text-[12.5px] text-slate-500 italic leading-none">.</span>
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* ── Input area ── */}
              <div className={cn(
                "shrink-0 border-t border-[#E2E8F0] bg-white px-4 pb-3 pt-3",
                isPageMode && "bg-white px-10 pb-5 pt-3"
              )}>
                {/* Quick chips */}
                {display.length <= 1 && !isTyping && !isPageMode && (
                  <div className="mb-3 flex flex-wrap gap-1.5">
                    {[
                      'Create a template',
                      'Launch a campaign',
                      'Show contacts',
                      'Open inbox',
                      'View analytics',
                      'Report an issue',
                    ].map(s => (
                      <button key={s} type="button" disabled={isTyping}
                        onClick={() => sendMessage(s)}
                        className="rounded-full border border-violet-200/80 bg-gradient-to-b from-white to-violet-50/60 px-3 py-1.5 text-[11.5px] font-medium text-violet-700 shadow-[0_1px_3px_rgba(109,40,217,0.1)] transition-all hover:border-violet-400 hover:shadow-[0_2px_6px_rgba(109,40,217,0.2)] disabled:opacity-40">
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                {!showUpload && (
                  <form onSubmit={e => { e.preventDefault(); handleSend(); }}>
                    {isPageMode && showPageEmptyState && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {[
                          'Show today’s analytics',
                          'Create a campaign',
                          'How many orders this month?',
                          'Build an automation flow',
                        ].map((prompt) => (
                          <button
                            key={prompt}
                            type="button"
                            onClick={() => sendMessage(prompt)}
                            className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-[11.5px] font-medium text-violet-700 transition-colors hover:border-violet-300 hover:bg-violet-100"
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className={cn(
                      "flex items-end gap-2 rounded-2xl border-2 border-slate-200 bg-slate-50/80 px-4 py-2.5 transition-all duration-150",
                      "focus-within:border-green-300 focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(34,197,94,0.10)]",
                      isPageMode && "rounded-[24px] border-green-100 bg-[linear-gradient(180deg,#ffffff_0%,#f0fdf4_100%)] px-5 py-3 shadow-[0_14px_35px_-24px_rgba(34,197,94,0.18)]"
                    )}>
                      <textarea
                        ref={textareaRef}
                        placeholder={isPageMode ? "Ask GenUI to analyse, create, navigate, or build something..." : "Ask Aria anything..."}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        rows={1}
                        disabled={isTyping}
                        className={cn(
                          "min-h-[32px] flex-1 resize-none bg-transparent pt-1 text-[13.5px] text-slate-700 outline-none placeholder:text-slate-400 disabled:opacity-50",
                          isPageMode && "min-h-[44px] text-[15px] leading-6"
                        )}
                      />
                      <button
                        type="button"
                        onClick={handleMicClick}
                        disabled={isTyping}
                        title={isRecording ? 'Stop recording' : 'Speak your message'}
                        className={cn(
                          "mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-all disabled:opacity-30",
                          isRecording
                            ? "animate-pulse bg-red-500 text-white shadow-sm hover:bg-red-600"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700",
                          isPageMode && "h-10 w-10 rounded-[18px]"
                        )}
                      >
                        {isTranscribing
                          ? <Loader2 size={13} className="animate-spin" />
                          : isRecording
                            ? <MicOff size={13} />
                            : <Mic size={13} />
                        }
                      </button>
                      <Button type="submit" size="icon" disabled={!input.trim() || isTyping}
                        className={cn(
                          "mb-0.5 h-8 w-8 shrink-0 rounded-xl bg-[#22C55E] text-white shadow-sm transition-all hover:scale-105 hover:bg-[#16A34A] disabled:opacity-30",
                          isPageMode && "h-10 w-10 rounded-[18px]"
                        )}>
                        <Send size={13} />
                      </Button>
                    </div>
                  </form>
                )}

                <p className={cn("mt-2 text-center text-[10px] text-slate-400", isPageMode && "mt-3")}>
                  Aria · PhotonX AI · Your data stays private
                </p>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

// ── Tool result cards ─────────────────────────────────────────────────────────
const DATA_TOOLS = new Set([
  'get_templates', 'search_templates', 'search_contacts', 'list_campaigns',
  'get_dashboard_stats', 'get_conversations', 'list_orders',
  'navigate_to_page', 'get_tags', 'get_team_members', 'get_profile',
  'get_analytics', 'get_subscription_info', 'get_campaign_analytics',
  'list_automation_flows', 'automation_list_flows', 'send_whatsapp_message', 'send_template_to_contact',
  'get_onboarding_guide',
  'create_automation_flow', 'automation_create_flow', 'add_flow_node', 'update_flow_node', 'connect_flow_nodes', 'save_current_flow',
  'list_bookings', 'list_event_types', 'get_availability', 'book_appointment',
  'get_social_posts', 'get_social_accounts', 'generate_ai_image',
  'get_commerce_stats', 'list_products', 'get_template_categories',
  'show_campaign_form', 'get_campaign_recipients', 'contacts_show_tag_form',

  // New GenUI Tool names
  'inbox_list_conversations', 'inbox_get_conversation', 'inbox_send_message',
  'inbox_assign_agent', 'inbox_assign_to_member', 'inbox_mark_read',
  'inbox_get_unread_count', 'inbox_search_conversations', 'inbox_get_summary',
  'inbox_mark_all_read', 'inbox_get_messages', 'inbox_create_conversation',
  'inbox_step_in', 'inbox_star_message', 'inbox_unstar_message',
  'inbox_get_starred_messages', 'inbox_forward_message', 'inbox_get_starred_conversations',
  'inbox_add_reaction', 'inbox_remove_reaction', 'inbox_close_conversation', 'inbox_reopen_conversation',

  'contacts_list', 'contacts_get', 'contacts_create', 'contacts_update',
  'contacts_search', 'contacts_add_tags', 'contacts_show_tag_form',
  'contacts_assign_tag', 'contacts_delete', 'contacts_list_tags',
  'contacts_delete_tag', 'contacts_block', 'contacts_unblock',
  'contacts_bulk_add_tags', 'contacts_pause_flows', 'contacts_remove_tags',
  'contacts_update_tag', 'contacts_get_stats', 'contacts_get_growth',
  'contacts_bulk_import', 'templates_create', 'campaigns_create'
]);

type SendFn = (text: string) => void;
type NavFn = (to: string) => void;

function ToolResultRenderer({ toolCalled, toolResult, sendMessage, navigate, onAction, toolCallId }: { toolCalled: string; toolResult: any; sendMessage: SendFn; navigate: NavFn; onAction: (action: any) => void; toolCallId: string }) {
  if (!toolResult?.success) return null;
  switch (toolCalled) {
    case 'get_templates':
    case 'search_templates': return <TemplatesCard data={toolResult} sendMessage={sendMessage} />;
    case 'search_contacts': return <ContactsCard data={toolResult} sendMessage={sendMessage} />;
    case 'list_campaigns': return <CampaignsCard data={toolResult} sendMessage={sendMessage} />;
    case 'get_dashboard_stats': return <StatsCard data={toolResult} sendMessage={sendMessage} />;
    case 'get_conversations': return <ConversationsCard data={toolResult} navigate={navigate} />;
    case 'list_orders': return <OrdersCard data={toolResult} navigate={navigate} sendMessage={sendMessage} />;
    case 'navigate_to_page':
      return (
        <GenUIComponentRenderer
          toolName="navigate_to_page"
          toolCallId={toolCallId}
          data={toolResult}
          componentHint="NavigationCard"
          status="completed"
          onAction={onAction}
        />
      );
    case 'get_tags': return <TagsCard data={toolResult} sendMessage={sendMessage} />;
    case 'get_team_members': return <TeamMembersCard data={toolResult} sendMessage={sendMessage} />;
    case 'get_profile': return <ProfileCard data={toolResult} navigate={navigate} />;
    case 'get_analytics': return <AnalyticsCard data={toolResult} />;
    case 'get_subscription_info': return <SubscriptionCard data={toolResult} navigate={navigate} />;
    case 'get_campaign_analytics': return toolResult?.all_campaigns
      ? <CampaignsCard data={toolResult} sendMessage={sendMessage} />
      : <CampaignAnalyticsCard data={toolResult} />;
    case 'list_automation_flows':
    case 'automation_list_flows': return <AutomationFlowsCard data={toolResult} navigate={navigate} />;
    case 'send_whatsapp_message': return <SendMessageCard data={toolResult} />;
    case 'send_template_to_contact': return <SendMessageCard data={toolResult} />;
    case 'get_onboarding_guide': return <OnboardingGuideCard data={toolResult} navigate={navigate} />;
    case 'create_automation_flow':
    case 'automation_create_flow': return <CreateFlowCard data={toolResult} navigate={navigate} />;
    case 'add_flow_node': return <AddNodeCard data={toolResult} />;
    case 'update_flow_node': return <UpdateNodeCard data={toolResult} />;
    case 'connect_flow_nodes': return <ConnectNodesCard data={toolResult} />;
    case 'save_current_flow': return <SaveFlowCard data={toolResult} />;
    case 'list_bookings': return <BookingsCard data={toolResult} navigate={navigate} />;
    case 'list_event_types': return <EventTypesCard data={toolResult} navigate={navigate} />;
    case 'get_social_posts': return <SocialPostsCard data={toolResult} navigate={navigate} />;
    case 'get_social_accounts': return <SocialAccountsCard data={toolResult} navigate={navigate} />;
    case 'generate_ai_image': return <AiImageCard data={toolResult} />;
    case 'get_template_categories': return <TemplateCategoriesCard data={toolResult} sendMessage={sendMessage} />;
    case 'get_commerce_stats': return <CommerceStatsCard data={toolResult} navigate={navigate} />;
    case 'list_products': return <ProductsCard data={toolResult} navigate={navigate} />;
    case 'show_campaign_form': return <CampaignFormCard data={toolResult} sendMessage={sendMessage} />;
    case 'get_campaign_recipients': return <CampaignRecipientsCard data={toolResult} sendMessage={sendMessage} />;
    case 'templates_create':
      return (
        <GenUIComponentRenderer
          toolName="templates_create"
          toolCallId={toolCallId}
          data={toolResult}
          componentHint="TemplateFormCard"
          status="completed"
          onAction={onAction}
        />
      );
    case 'campaigns_create':
      return (
        <GenUIComponentRenderer
          toolName="campaigns_create"
          toolCallId={toolCallId}
          data={toolResult}
          componentHint="CampaignDetailCard"
          status="completed"
          onAction={onAction}
        />
      );
    default:
      if (toolResult?.componentHint) {
        return (
          <GenUIComponentRenderer
            toolName={toolCalled}
            toolCallId={toolCallId}
            data={toolResult}
            componentHint={toolResult.componentHint}
            status="completed"
            onAction={onAction}
          />
        );
      }
      return null;
  }
}

// ── Card header ───────────────────────────────────────────────────────────────
function CardHeader({ label, count }: { label: string; count?: number | string }) {
  return (
    <div className="flex items-center justify-between border-b border-violet-100 bg-gradient-to-r from-violet-50 to-violet-50/40 px-3.5 py-2">
      <span className="text-[10.5px] font-bold uppercase tracking-widest text-violet-600">{label}</span>
      {count !== undefined && (
        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-600">
          {count}
        </span>
      )}
    </div>
  );
}

// ── Templates card ────────────────────────────────────────────────────────────
const TEMPLATE_STATUS: Record<string, { bg: string; text: string; dot: string }> = {
  APPROVED: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  PENDING: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', dot: 'bg-amber-400' },
  REJECTED: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', dot: 'bg-red-400' },
};
function TemplatesCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const [expanded, setExpanded] = React.useState(false);
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null);
  const templates: any[] = data.templates || [];
  const visible = expanded ? templates : templates.slice(0, 8);
  const remaining = templates.length - 8;
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Templates" count={`${data.count} found`} />
      {/* Column headers */}
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-3.5 py-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Template</span>
        <span className="w-[72px] text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">Category</span>
        <span className="w-[44px] text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">Lang</span>
        <span className="w-[74px] text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">Status</span>
        <span className="w-[24px]" />
      </div>
      <div className="divide-y divide-slate-100/80">
        {visible.map((t, i) => {
          const sc = TEMPLATE_STATUS[t.status] ?? { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', dot: 'bg-slate-400' };
          const isHovered = hoveredIdx === i;
          const preview = buildTemplatePreview(t);
          return (
            <div
              key={i}
              className="group relative grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-2 px-3.5 py-2.5 transition-colors hover:bg-violet-50/50 cursor-default"
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Template name + body preview */}
              <div className="min-w-0">
                <p className="truncate font-mono text-[12px] font-semibold text-slate-700">{t.name}</p>
                {t.body_text && (
                  <p className="mt-0.5 truncate text-[10.5px] text-violet-500 leading-tight">{t.body_text}</p>
                )}
              </div>
              {/* Category badge */}
              <div className="flex w-[72px] justify-center">
                <span className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[9.5px] font-semibold text-blue-600 capitalize truncate max-w-full">
                  {(t.category || 'Marketing').replace(/_/g, ' ').toLowerCase()}
                </span>
              </div>
              {/* Language */}
              <div className="flex w-[44px] justify-center">
                <span className="text-[10.5px] font-medium text-slate-500">{(t.language || 'en').replace('_', ' ')}</span>
              </div>
              {/* Status */}
              <div className="flex w-[74px] justify-center">
                <span className={cn("flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[9.5px] font-semibold", sc.bg, sc.text)}>
                  <span className={cn("h-1.5 w-1.5 rounded-full", sc.dot)} />
                  {t.status ? t.status.charAt(0) + t.status.slice(1).toLowerCase() : 'Draft'}
                </span>
              </div>
              {/* Action */}
              <div className="flex w-[24px] justify-center">
                {t.status === 'APPROVED' ? (
                  <button
                    type="button"
                    onClick={() => sendMessage(`Use the "${t.name}" template for a new campaign`)}
                    className="hidden rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 transition-colors hover:bg-violet-200 group-hover:block whitespace-nowrap"
                  >
                    Use →
                  </button>
                ) : (
                  <span className="text-slate-300 select-none">···</span>
                )}
              </div>
              {/* WhatsApp phone preview popup on hover */}
              {isHovered && preview && (
                <div className="pointer-events-none absolute right-0 top-1/2 z-50 -translate-y-1/2 translate-x-[calc(100%+8px)]" style={{ minWidth: 220 }}>
                  <WhatsAppPhonePreview template={t} preview={preview} />
                </div>
              )}
            </div>
          );
        })}
        {!expanded && remaining > 0 && (
          <button type="button" onClick={() => setExpanded(true)}
            className="w-full px-3.5 py-2 text-center text-[11px] font-medium text-violet-500 transition-colors hover:bg-violet-50">
            Show {remaining} more template{remaining > 1 ? 's' : ''} ↓
          </button>
        )}
      </div>
    </div>
  );
}

/** Build a preview object from template components */
function buildTemplatePreview(t: any) {
  if (!t) return null;
  const components: any[] = Array.isArray(t.components) ? t.components : [];
  const header = components.find((c: any) => c.type === 'HEADER');
  const body = components.find((c: any) => c.type === 'BODY');
  const footer = components.find((c: any) => c.type === 'FOOTER');
  const buttons = components.find((c: any) => c.type === 'BUTTONS');
  const bodyText = t.body_text || body?.text || null;
  if (!bodyText) return null;
  return { header, bodyText, footer, buttons };
}

/** WhatsApp phone mockup preview card — shows on template row hover */
function WhatsAppPhonePreview({ template, preview }: { template: any; preview: any }) {
  const { header, bodyText, footer, buttons } = preview;
  const btns: any[] = buttons?.buttons || [];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_12px_40px_-8px_rgba(0,0,0,0.22)] overflow-hidden animate-in fade-in slide-in-from-right-2 duration-150" style={{ width: 220 }}>
      {/* Phone top bar */}
      <div className="flex items-center gap-2 bg-[#075E54] px-3 py-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#128C7E] text-[11px] font-bold text-white">W</div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-white leading-none">WhatsApp Business</p>
          <p className="text-[9px] text-emerald-300 mt-0.5">● Online</p>
        </div>
      </div>
      {/* Chat area */}
      <div className="min-h-[80px] px-2 py-2" style={{ background: '#e5ddd5' }}>
        {/* Date chip */}
        <div className="mb-2 flex justify-center">
          <span className="rounded-full bg-white/70 px-2 py-0.5 text-[8.5px] text-slate-500 shadow-sm">TODAY</span>
        </div>
        {/* Message bubble */}
        <div className="ml-auto max-w-[92%] overflow-hidden rounded-lg bg-white shadow-sm">
          {/* Header text */}
          {header?.format === 'TEXT' && header.text && (
            <div className="border-b border-slate-100 px-2.5 pt-2 pb-1">
              <p className="text-[10.5px] font-bold text-slate-800 leading-tight">{header.text}</p>
            </div>
          )}
          {/* Body */}
          <div className="px-2.5 pt-1.5 pb-1">
            <p className="text-[10px] leading-relaxed text-slate-700 whitespace-pre-wrap line-clamp-6">{bodyText}</p>
          </div>
          {/* Footer */}
          {footer?.text && (
            <div className="border-t border-slate-100 px-2.5 py-1">
              <p className="text-[9px] text-slate-400">{footer.text}</p>
            </div>
          )}
          {/* Timestamp */}
          <div className="flex justify-end px-2 pb-1">
            <span className="text-[8px] text-slate-400">now ✓✓</span>
          </div>
          {/* Buttons */}
          {btns.length > 0 && (
            <div className="border-t border-slate-100">
              {btns.slice(0, 3).map((btn: any, bi: number) => (
                <div key={bi} className={cn("flex items-center justify-center gap-1 px-2 py-1.5 text-[9.5px] font-semibold text-[#128C7E]", bi > 0 && "border-t border-slate-100")}>
                  <span>{btn.type === 'URL' ? '🔗' : btn.type === 'PHONE_NUMBER' ? '📞' : '↩'}</span>
                  <span>{btn.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Template name footer */}
      <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5">
        <p className="truncate text-[9px] font-mono text-slate-400">{template.name}</p>
      </div>
    </div>
  );
}

// ── Campaign Form card ────────────────────────────────────────────────────────
function CampaignFormCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const templates: any[] = data.templates || [];
  const tags: any[] = data.tags || [];
  const totalContacts: number = data.totalContacts || 0;

  const [name, setName] = React.useState('');
  const [selectedTemplate, setSelectedTemplate] = React.useState<any>(null);
  const [templateSearch, setTemplateSearch] = React.useState('');
  const [showTemplateList, setShowTemplateList] = React.useState(false);
  const [recipientMode, setRecipientMode] = React.useState<'all' | 'tag' | 'csv'>('all');
  const [selectedTag, setSelectedTag] = React.useState<any>(null);
  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [csvPhones, setCsvPhones] = React.useState<string[]>([]);
  const [scheduleMode, setScheduleMode] = React.useState<'now' | 'later'>('now');
  const [scheduleAt, setScheduleAt] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);

  // Close template dropdown when clicking outside
  const templateDropdownRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!showTemplateList) return;
    function handleClickOutside(e: MouseEvent) {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setShowTemplateList(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTemplateList]);

  // Parse CSV file for phone numbers
  function handleCsvChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      // Extract phone-like values from any column — strip whitespace, keep rows with digits
      const lines = text.split('\n').slice(1); // skip header
      const phones = lines
        .map(l => l.split(',')[0].replace(/[^+\d]/g, '').trim())
        .filter(p => p.length >= 7);
      setCsvPhones(phones);
    };
    reader.readAsText(file);
  }

  const filteredTemplates = templates.filter(t =>
    !templateSearch || t.name?.toLowerCase().includes(templateSearch.toLowerCase())
  );

  const recipientCount = recipientMode === 'all'
    ? totalContacts
    : recipientMode === 'tag'
      ? (selectedTag?.count ?? 0)
      : csvPhones.length;

  const canSubmit = name.trim() && selectedTemplate && (
    recipientMode !== 'csv' || csvPhones.length > 0
  );

  function handleSubmit() {
    if (!canSubmit) return;
    // Include IDs directly so the AI doesn't need to look them up
    let msg = `Create a campaign named "${name.trim()}" using template "${selectedTemplate.name}" (template_id: ${selectedTemplate.id})`;
    if (recipientMode === 'tag' && selectedTag) {
      msg += ` — send to contacts with tag "${selectedTag.name}" (tag_id: ${selectedTag.id})`;
    } else if (recipientMode === 'csv' && csvPhones.length > 0) {
      msg += ` — send to ${csvPhones.length} contacts from CSV: ${csvPhones.slice(0, 5).join(', ')}${csvPhones.length > 5 ? ` and ${csvPhones.length - 5} more` : ''}`;
    } else {
      msg += ` — send to all contacts`;
    }
    if (scheduleMode === 'later' && scheduleAt) {
      msg += ` — schedule it for ${scheduleAt}`;
    } else {
      msg += ` — send now`;
    }
    setSubmitted(true);
    sendMessage(msg);
  }

  if (submitted) {
    return (
      <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
        <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-xl">🚀</span>
          <p className="text-[13px] font-semibold text-slate-700">Campaign submitted!</p>
          <p className="text-[11px] text-slate-400">Aria is creating your campaign…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="New Campaign" count={`${templates.length} templates`} />
      <div className="divide-y divide-slate-100">

        {/* Campaign name */}
        <div className="px-3.5 py-2.5">
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Campaign name</label>
          <input
            type="text"
            placeholder="e.g. Diwali Sale 2025"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12.5px] text-slate-700 outline-none placeholder:text-slate-300 focus:border-violet-300 focus:bg-white focus:ring-2 focus:ring-violet-100"
          />
        </div>

        {/* Template picker */}
        <div className="px-3.5 py-2.5">
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Template</label>
          {selectedTemplate ? (
            <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-1.5">
              <Megaphone className="h-3.5 w-3.5 shrink-0 text-violet-500" />
              <span className="flex-1 truncate font-mono text-[12px] text-violet-700">{selectedTemplate.name}</span>
              <button type="button" onClick={() => { setSelectedTemplate(null); setShowTemplateList(true); }}
                className="text-[10px] text-violet-400 hover:text-violet-600">Change</button>
            </div>
          ) : (
            <div className="relative" ref={templateDropdownRef}>
              <input
                type="text"
                placeholder="Search approved templates…"
                value={templateSearch}
                onChange={e => { setTemplateSearch(e.target.value); setShowTemplateList(true); }}
                onFocus={() => setShowTemplateList(true)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12.5px] text-slate-700 outline-none placeholder:text-slate-300 focus:border-violet-300 focus:bg-white focus:ring-2 focus:ring-violet-100"
              />
              {showTemplateList && (
                <div className="absolute z-10 mt-1 max-h-44 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {filteredTemplates.slice(0, 20).map((t, i) => (
                    <button
                      key={i}
                      type="button"
                      onMouseDown={e => e.preventDefault()} // prevent blur before click
                      onClick={() => { setSelectedTemplate(t); setShowTemplateList(false); setTemplateSearch(''); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 hover:bg-violet-50"
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                      <span className="flex-1 truncate font-mono">{t.name}</span>
                    </button>
                  ))}
                  {filteredTemplates.length === 0 && (
                    <div className="px-3 py-2 text-[11px] text-slate-400">No matching templates</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recipients */}
        <div className="px-3.5 py-2.5">
          <label className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Recipients</label>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => { setRecipientMode('all'); setSelectedTag(null); setCsvFile(null); setCsvPhones([]); }}
              className={cn(
                "flex flex-1 items-center justify-center gap-1 rounded-lg border px-1.5 py-1.5 text-[11px] font-medium transition-all",
                recipientMode === 'all'
                  ? "border-violet-300 bg-violet-50 text-violet-700"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
              )}
            >
              <Users className="h-3 w-3 shrink-0" />
              All
              <span className="rounded-full bg-violet-100 px-1 py-0 text-[9.5px] font-bold text-violet-600">{totalContacts.toLocaleString()}</span>
            </button>
            {tags.length > 0 && (
              <button
                type="button"
                onClick={() => { setRecipientMode('tag'); setCsvFile(null); setCsvPhones([]); }}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1 rounded-lg border px-1.5 py-1.5 text-[11px] font-medium transition-all",
                  recipientMode === 'tag'
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                )}
              >
                <Tag className="h-3 w-3 shrink-0" />
                By tag
              </button>
            )}
            <button
              type="button"
              onClick={() => { setRecipientMode('csv'); setSelectedTag(null); }}
              className={cn(
                "flex flex-1 items-center justify-center gap-1 rounded-lg border px-1.5 py-1.5 text-[11px] font-medium transition-all",
                recipientMode === 'csv'
                  ? "border-violet-300 bg-violet-50 text-violet-700"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
              )}
            >
              <Upload className="h-3 w-3 shrink-0" />
              CSV
            </button>
          </div>

          {/* Tag chips */}
          {recipientMode === 'tag' && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tags.map((tag, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedTag(selectedTag?.id === tag.id ? null : tag)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-all",
                    selectedTag?.id === tag.id
                      ? "border-violet-400 bg-violet-100 text-violet-700"
                      : "border-slate-200 bg-white text-slate-500 hover:border-violet-200 hover:bg-violet-50"
                  )}
                >
                  {tag.name}
                  {tag.count > 0 && <span className="ml-1 text-[10px] opacity-60">{tag.count}</span>}
                </button>
              ))}
            </div>
          )}

          {/* CSV upload */}
          {recipientMode === 'csv' && (
            <div className="mt-2">
              {csvFile ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  <span className="flex-1 truncate text-[11.5px] text-emerald-700 font-medium">{csvFile.name}</span>
                  <span className="text-[10px] text-emerald-500 font-semibold">{csvPhones.length} numbers</span>
                  <button type="button" onClick={() => { setCsvFile(null); setCsvPhones([]); }}
                    className="text-[10px] text-emerald-400 hover:text-emerald-600">Remove</button>
                </div>
              ) : (
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 transition-colors hover:border-violet-300 hover:bg-violet-50/50">
                  <Upload className="h-4 w-4 shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-slate-600">Upload CSV file</p>
                    <p className="text-[10.5px] text-slate-400">First column = phone numbers (with country code)</p>
                  </div>
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsvChange} />
                </label>
              )}
            </div>
          )}

          {recipientCount > 0 && (
            <p className="mt-1.5 text-[11px] text-slate-400">
              <span className="font-semibold text-violet-600">{recipientCount.toLocaleString()}</span> contacts will receive this campaign
            </p>
          )}
        </div>

        {/* Schedule */}
        <div className="px-3.5 py-2.5">
          <label className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Send timing</label>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setScheduleMode('now')}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11.5px] font-medium transition-all",
                scheduleMode === 'now'
                  ? "border-violet-300 bg-violet-50 text-violet-700"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
              )}
            >
              <Zap className="h-3 w-3" />
              Send now
            </button>
            <button
              type="button"
              onClick={() => setScheduleMode('later')}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11.5px] font-medium transition-all",
                scheduleMode === 'later'
                  ? "border-violet-300 bg-violet-50 text-violet-700"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
              )}
            >
              <Clock className="h-3 w-3" />
              Schedule
            </button>
          </div>
          {scheduleMode === 'later' && (
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={e => setScheduleAt(e.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] text-slate-700 outline-none focus:border-violet-300 focus:bg-white focus:ring-2 focus:ring-violet-100"
            />
          )}
        </div>

        {/* Submit */}
        <div className="px-3.5 py-3">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-[13px] font-semibold transition-all",
              canSubmit
                ? "bg-violet-600 text-white shadow-sm hover:bg-violet-700 active:scale-[0.98]"
                : "bg-slate-100 text-slate-400 cursor-not-allowed"
            )}
          >
            <Megaphone className="h-4 w-4" />
            Launch Campaign
          </button>
          {!name.trim() && !selectedTemplate && (
            <p className="mt-1.5 text-center text-[10.5px] text-slate-400">Fill in the name and pick a template to continue</p>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Template Categories card ──────────────────────────────────────────────────
function TemplateCategoriesCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const categories: any[] = data.categories || [];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="New Template" />
      <div className="p-3.5 space-y-3">
        <p className="text-[12.5px] text-slate-600 font-medium">What type of template do you want to create?</p>
        <div className="grid gap-2">
          {categories.map((cat, i) => (
            <button
              key={i}
              type="button"
              onClick={() => sendMessage(`I want to create a ${cat.name} template`)}
              className="group flex w-full items-start gap-3 rounded-xl border border-slate-100 p-3 text-left transition-all hover:border-violet-200 hover:bg-violet-50/50 hover:shadow-sm"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-[16px]">
                {cat.id === 'MARKETING' ? '🚀' : cat.id === 'UTILITY' ? '🛠️' : '🔐'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-bold text-slate-700">{cat.name}</p>
                <p className="text-[11px] text-slate-400 leading-tight">{cat.description}</p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 mt-0.5 text-slate-300 transition-transform group-hover:translate-x-0.5" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── Contacts card ─────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  'bg-violet-100 text-violet-700', 'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700', 'bg-indigo-100 text-indigo-700',
];

// ── Campaign recipients card ───────────────────────────────────────────────────
const DELIVERY_STATUS: Record<string, { label: string; cls: string }> = {
  read: { label: 'Read', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  delivered: { label: 'Delivered', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  sent: { label: 'Sent', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-200' },
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  replied: { label: 'Replied', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};
function CampaignRecipientsCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const [expanded, setExpanded] = React.useState(false);
  const allRecipients: any[] = data.recipients || [];
  const INITIAL_SHOW = 10;
  const visible = expanded ? allRecipients : allRecipients.slice(0, INITIAL_SHOW);
  const hiddenCount = allRecipients.length - INITIAL_SHOW;
  const initials = (name: string) => (name || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Campaign Recipients" count={`${data.total_count ?? allRecipients.length} total`} />
      <div className="divide-y divide-slate-100/80">
        {visible.map((r: any, i: number) => {
          const ds = DELIVERY_STATUS[r.status?.toLowerCase()] || { label: r.status || '—', cls: 'bg-slate-50 text-slate-500 border-slate-200' };
          return (
            <div key={i} className="group flex items-center gap-2.5 px-3.5 py-2.5 transition-colors hover:bg-violet-50/50">
              <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10.5px] font-bold", AVATAR_COLORS[i % AVATAR_COLORS.length])}>
                {initials(r.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-medium text-slate-700">{r.name || 'Unknown'}</p>
                <p className="truncate text-[11px] text-slate-400">{r.phone || '—'}</p>
              </div>
              <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium", ds.cls)}>{ds.label}</span>
              <button type="button"
                onClick={() => sendMessage(`Send a WhatsApp message to ${r.name}`)}
                className="hidden shrink-0 rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-200 group-hover:block">
                Message →
              </button>
            </div>
          );
        })}
        {!expanded && hiddenCount > 0 && (
          <button type="button" onClick={() => setExpanded(true)}
            className="w-full px-3.5 py-2.5 text-center text-[11px] font-medium text-[#22C55E] transition-colors hover:bg-green-50">
            + {hiddenCount} more — show all
          </button>
        )}
        {expanded && allRecipients.length > INITIAL_SHOW && (
          <button type="button" onClick={() => setExpanded(false)}
            className="w-full px-3.5 py-2 text-center text-[11px] font-medium text-slate-400 transition-colors hover:bg-slate-50">
            Show less ↑
          </button>
        )}
      </div>
    </div>
  );
}

function ContactsCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const [expanded, setExpanded] = React.useState(false);
  const allContacts: any[] = data.contacts || [];
  const INITIAL_SHOW = 10;
  const visible = expanded ? allContacts : allContacts.slice(0, INITIAL_SHOW);
  const hiddenCount = allContacts.length - INITIAL_SHOW;
  const initials = (name: string) => (name || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Contacts" count={`${data.count} found`} />
      <div className="divide-y divide-slate-100/80">
        {visible.map((c, i) => (
          <div key={i} className="group flex items-start gap-2.5 px-3.5 py-2.5 transition-colors hover:bg-violet-50/50">
            <div className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10.5px] font-bold", AVATAR_COLORS[i % AVATAR_COLORS.length])}>
              {initials(c.name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12.5px] font-medium text-slate-700">{c.name || 'Unknown'}</p>
              <p className="truncate text-[11px] text-slate-400">{c.phone || c.email || '—'}</p>
              {c.tags?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {c.tags.map((tag: string, ti: number) => (
                    <span key={ti} className="rounded-full border border-violet-100 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-600">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button type="button"
              onClick={() => sendMessage(`Send a WhatsApp message to ${c.name}`)}
              className="mt-0.5 hidden shrink-0 rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-200 group-hover:block">
              Message →
            </button>
          </div>
        ))}
        {!expanded && hiddenCount > 0 && (
          <button type="button" onClick={() => setExpanded(true)}
            className="w-full px-3.5 py-2.5 text-center text-[11px] font-medium text-[#22C55E] transition-colors hover:bg-green-50">
            + {hiddenCount} more contact{hiddenCount > 1 ? 's' : ''} — show all
          </button>
        )}
        {expanded && allContacts.length > INITIAL_SHOW && (
          <button type="button" onClick={() => setExpanded(false)}
            className="w-full px-3.5 py-2 text-center text-[11px] font-medium text-slate-400 transition-colors hover:bg-slate-50">
            Show less ↑
          </button>
        )}
      </div>
    </div>
  );
}

// ── Campaigns card ────────────────────────────────────────────────────────────
const CAMPAIGN_STATUS: Record<string, { dot: string; badge: string }> = {
  completed: { dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  running: { dot: 'bg-blue-500', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  scheduled: { dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  draft: { dot: 'bg-slate-400', badge: 'bg-slate-50 text-slate-600 border-slate-200' },
  failed: { dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
};

function CampaignsCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const campaigns: any[] = (data.campaigns || []).slice(0, 6);
  const total = data.total_count ?? data.count ?? campaigns.length;
  const remaining = total - campaigns.length;
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Campaigns" count={total > 0 ? `${total} found` : undefined} />
      <div className="divide-y divide-slate-100/80">
        {campaigns.length === 0 ? (
          <div className="px-4 py-5 text-center text-[12px] text-slate-400">
            No campaigns yet. Ask me to create one!
          </div>
        ) : (
          campaigns.map((c, i) => {
            const sc = CAMPAIGN_STATUS[c.status?.toLowerCase()] ?? { dot: 'bg-slate-400', badge: 'bg-slate-50 text-slate-600 border-slate-200' };
            return (
              <div key={i} className="group flex items-center gap-2.5 px-3.5 py-2 transition-colors hover:bg-violet-50/50">
                <span className={cn("mt-0.5 h-2 w-2 shrink-0 rounded-full", sc.dot)} />
                <span className="flex-1 truncate font-mono text-[12px] text-slate-700">{c.name}</span>
                <button type="button"
                  onClick={() => sendMessage(`Show analytics for the "${c.name}" campaign`)}
                  className="hidden shrink-0 rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-200 group-hover:block">
                  Analytics →
                </button>
                <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize group-hover:hidden", sc.badge)}>
                  {c.status}
                </span>
              </div>
            );
          })
        )}
        {remaining > 0 && (
          <div className="px-3.5 py-2 text-center text-[11px] text-slate-400">+ {remaining} more</div>
        )}
      </div>
    </div>
  );
}

// ── Stats card ────────────────────────────────────────────────────────────────
function StatsCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const s = data.stats || {};
  const metrics = [
    { label: 'Messages', value: s.total_messages ?? s.messages_sent ?? s.messages ?? '—', icon: '📨', action: 'show me message analytics' },
    { label: 'Conversations', value: s.total_conversations ?? s.conversations ?? '—', icon: '💬', action: 'show me my conversations' },
    { label: 'Campaigns', value: s.total_campaigns ?? s.campaigns ?? '—', icon: '📢', action: 'show me all campaigns' },
    { label: 'Contacts', value: s.total_contacts ?? s.contacts ?? '—', icon: '👥', action: 'show me all contacts' },
  ];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Dashboard Overview" />
      <div className="grid grid-cols-2 divide-x divide-y divide-slate-100">
        {metrics.map((m, i) => (
          <div key={i} role="button" tabIndex={0}
            onClick={() => sendMessage(m.action)}
            onKeyDown={e => e.key === 'Enter' && sendMessage(m.action)}
            className="group flex cursor-pointer flex-col px-4 py-3 transition-colors hover:bg-violet-50/60 active:bg-violet-100/50">
            <span className="text-[20px] font-bold tracking-tight text-slate-800">
              {typeof m.value === 'number' ? m.value.toLocaleString() : m.value}
            </span>
            <span className="mt-0.5 flex items-center justify-between text-[11px] text-slate-400">
              <span>{m.icon} {m.label}</span>
              <span className="hidden text-[10px] text-violet-500 group-hover:block">View →</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Conversations card ────────────────────────────────────────────────────────
const CONV_STATUS: Record<string, { dot: string; badge: string }> = {
  open: { dot: 'bg-blue-500', badge: 'bg-blue-50 text-blue-700' },
  pending: { dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700' },
  resolved: { dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700' },
};

function ConversationsCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const totalCount = Number(data.count ?? data.total_count ?? data.total ?? (data.conversations || []).length);
  const convs: any[] = (data.conversations || []).slice(0, 5);
  const remaining = Math.max(0, totalCount - convs.length);
  if (convs.length === 0) {
    return (
      <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
        <CardHeader label="Conversations" count="0 found" />
        <div className="px-3.5 py-5 text-center text-[12px] text-slate-400">
          No conversations found.
        </div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Conversations" count={`${totalCount} found`} />
      <div className="divide-y divide-slate-100/80">
        {convs.map((c, i) => {
          const sc = CONV_STATUS[c.status?.toLowerCase()] ?? { dot: 'bg-slate-400', badge: 'bg-slate-50 text-slate-600' };
          const conversationId = c.conversationId || c.id;
          const inboxRoute = conversationId ? `/home/inbox?conversation=${conversationId}` : '/home/inbox';
          return (
            <div key={i} role="button" tabIndex={0}
              onClick={() => navigate(inboxRoute)}
              onKeyDown={e => e.key === 'Enter' && navigate(inboxRoute)}
              className="group flex cursor-pointer items-center gap-2.5 px-3.5 py-2 transition-colors hover:bg-violet-50/60 active:bg-violet-100/60">
              <span className={cn("mt-0.5 h-2 w-2 shrink-0 rounded-full", sc.dot)} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-medium text-slate-700">{c.contact || c.contact_name || 'Unknown'}</p>
                {c.last_message && <p className="truncate text-[11px] text-slate-400">{c.last_message}</p>}
              </div>
              <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize group-hover:hidden", sc.badge)}>
                {c.status}
              </span>
              <span className="hidden shrink-0 rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 group-hover:block">
                Open →
              </span>
            </div>
          );
        })}
        {remaining > 0 && <div className="px-3.5 py-2 text-center text-[11px] text-slate-400">+ {remaining} more</div>}
      </div>
      <div className="border-t border-slate-100 p-2.5">
        <button type="button" onClick={() => navigate('/home/inbox')}
          className="w-full rounded-lg py-2 text-[11.5px] font-medium text-violet-600 transition-colors hover:bg-violet-50">
          Open Inbox →
        </button>
      </div>
    </div>
  );
}

// ── Orders card ───────────────────────────────────────────────────────────────
const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-600 border-amber-200',
  processing: 'bg-blue-50 text-blue-600 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  cancelled: 'bg-red-50 text-red-500 border-red-200',
  failed: 'bg-red-50 text-red-500 border-red-200',
  'on-hold': 'bg-slate-50 text-slate-500 border-slate-200',
};
function OrdersCard({ data, navigate, sendMessage }: { data: any; navigate: NavFn; sendMessage: SendFn }) {
  const orders: any[] = (data.orders || []).slice(0, 5);
  const remaining = (data.count || 0) - orders.length;
  const sourceLabel = data.source || '';
  const statusFilter = data.status_filter;
  const headerLabel = [sourceLabel, statusFilter ? `${statusFilter} orders` : 'orders'].filter(Boolean).join(' — ');
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label={headerLabel || 'Orders'} count={`${data.count} found`} />
      {orders.length === 0 ? (
        <div className="px-3.5 py-5 text-center text-[12px] text-slate-400">
          No {statusFilter || ''} orders found{sourceLabel ? ` in ${sourceLabel}` : ''}.
        </div>
      ) : (
        <div className="divide-y divide-slate-100/80">
          {orders.map((o, i) => {
            const statusKey = (o.status || '').toLowerCase();
            const statusCls = STATUS_COLOR[statusKey] || 'bg-slate-50 text-slate-500 border-slate-200';
            const customerName = o.customer || 'Unknown';
            return (
              <div key={i} className="group flex items-center justify-between px-3.5 py-2 transition-colors hover:bg-violet-50/40">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-medium text-slate-700">{customerName}</p>
                  <p className="text-[11px] text-slate-400">#{String(o.id || '').slice(0, 10)}</p>
                </div>
                <div className="ml-2 flex items-center gap-2">
                  <button type="button"
                    onClick={() => sendMessage(`Send a WhatsApp message to ${customerName} about their order`)}
                    className="hidden shrink-0 rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 transition-colors hover:bg-violet-200 group-hover:block">
                    Message →
                  </button>
                  <div className="flex flex-col items-end gap-0.5">
                    {o.total && <span className="text-[12.5px] font-semibold text-slate-700">₹{Number(o.total).toLocaleString()}</span>}
                    <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize', statusCls)}>{statusKey}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {remaining > 0 && <div className="px-3.5 py-2 text-center text-[11px] text-slate-400">+ {remaining} more</div>}
        </div>
      )}
      <div className="border-t border-slate-100 p-2.5">
        <button
          type="button"
          onClick={() => navigate('/home/orders')}
          className="w-full rounded-lg py-2 text-[11.5px] font-medium text-violet-600 transition-colors hover:bg-violet-50"
        >
          View all orders →
        </button>
      </div>
    </div>
  );
}

// ── Navigation card ───────────────────────────────────────────────────────────
interface NavPageMeta {
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
  bgGradient: string;
  chips?: string[];
}

const NAV_PAGE_META: Record<string, NavPageMeta> = {
  '/home/dashboard': {
    icon: LayoutDashboard, title: 'Dashboard', description: 'Overview of your messages, campaigns, and key business metrics.',
    gradient: 'from-violet-500 to-purple-600', bgGradient: 'from-violet-50 to-purple-50',
  },
  '/home/inbox': {
    icon: MessageSquare, title: 'Inbox', description: 'Manage all your WhatsApp conversations and customer messages in real time.',
    gradient: 'from-blue-500 to-indigo-600', bgGradient: 'from-blue-50 to-indigo-50',
  },
  '/home/contacts': {
    icon: Users, title: 'Contacts', description: 'Browse, search, and manage your CRM contacts and customer list.',
    gradient: 'from-emerald-500 to-teal-600', bgGradient: 'from-emerald-50 to-teal-50',
  },
  '/home/campaigns': {
    icon: Bell, title: 'Campaigns', description: 'Create and manage broadcast campaigns to reach your audience at scale.',
    gradient: 'from-amber-500 to-orange-500', bgGradient: 'from-amber-50 to-orange-50',
  },
  '/home/templates': {
    icon: FileText, title: 'Templates', description: 'Design and manage WhatsApp message templates for campaigns and quick replies.',
    gradient: 'from-fuchsia-500 to-pink-600', bgGradient: 'from-fuchsia-50 to-pink-50',
  },
  '/home/automation': {
    icon: Zap, title: 'Automation', description: 'Build intelligent chatbot flows and automated conversation sequences.',
    gradient: 'from-yellow-500 to-amber-500', bgGradient: 'from-yellow-50 to-amber-50',
  },
  '/home/analytics': {
    icon: BarChart2, title: 'Analytics', description: 'Dive deep into message delivery, open rates, and campaign performance data.',
    gradient: 'from-sky-500 to-blue-600', bgGradient: 'from-sky-50 to-blue-50',
  },
  '/home/integrations': {
    icon: Globe, title: 'Integrations', description: 'Connect your store, payment gateway, or custom API to supercharge your workflows.',
    gradient: 'from-violet-500 to-indigo-600', bgGradient: 'from-violet-50 to-indigo-50',
    chips: ['WooCommerce', 'Shopify', 'Razorpay', 'Custom API', 'PetPooja'],
  },
  '/home/woocommerce': {
    icon: ShoppingBag, title: 'WooCommerce', description: 'Connect your WooCommerce store to sync products, orders, and abandoned carts.',
    gradient: 'from-violet-500 to-purple-600', bgGradient: 'from-violet-50 to-purple-50',
  },
  '/home/shopify': {
    icon: ShoppingBag, title: 'Shopify', description: 'Connect your Shopify store to sync products and orders seamlessly.',
    gradient: 'from-green-400 to-emerald-500', bgGradient: 'from-green-50 to-emerald-50',
  },
  '/home/razorpay': {
    icon: CreditCard, title: 'Razorpay', description: 'Connect Razorpay to process payments and send payment links via WhatsApp.',
    gradient: 'from-sky-500 to-blue-600', bgGradient: 'from-sky-50 to-blue-50',
  },
  '/home/custom-api': {
    icon: Link2, title: 'Custom API', description: 'Connect your own API endpoints to extend platform functionality.',
    gradient: 'from-orange-500 to-red-500', bgGradient: 'from-orange-50 to-red-50',
  },
  '/home/commerce': {
    icon: Package, title: 'Commerce', description: 'Manage your WhatsApp catalog, product listings, and e-commerce orders.',
    gradient: 'from-rose-500 to-pink-600', bgGradient: 'from-rose-50 to-pink-50',
  },
  '/home/commerce/dashboard': {
    icon: Package, title: 'Commerce', description: 'Manage your WhatsApp catalog, product listings, and e-commerce orders.',
    gradient: 'from-rose-500 to-pink-600', bgGradient: 'from-rose-50 to-pink-50',
  },
  '/home/team': {
    icon: Users, title: 'Team', description: 'Manage your agents, assign roles, and configure team inbox access.',
    gradient: 'from-teal-500 to-cyan-600', bgGradient: 'from-teal-50 to-cyan-50',
  },
  '/home/scheduling': {
    icon: Clock, title: 'Scheduling', description: 'Schedule messages and campaigns to send at exactly the right time.',
    gradient: 'from-indigo-500 to-violet-500', bgGradient: 'from-indigo-50 to-violet-50',
  },
  '/home/settings': {
    icon: Settings, title: 'Settings', description: 'Configure your account, notifications, billing, and platform preferences.',
    gradient: 'from-slate-500 to-gray-600', bgGradient: 'from-slate-50 to-gray-50',
  },
  '/home/help': {
    icon: HelpCircle, title: 'Help & Support', description: 'Browse documentation, watch guides, or contact our support team.',
    gradient: 'from-violet-400 to-purple-500', bgGradient: 'from-violet-50 to-purple-50',
  },
};

const DEFAULT_NAV_META: NavPageMeta = {
  icon: Bot, title: 'Page', description: 'Navigate to this section of the platform.',
  gradient: 'from-violet-500 to-purple-600', bgGradient: 'from-violet-50 to-purple-50',
};

// Chip → integration route map for inline connect shortcuts
const CHIP_ROUTES: Record<string, string> = {
  WooCommerce: '/home/woocommerce',
  Shopify: '/home/shopify',
  Razorpay: '/home/razorpay',
  'Custom API': '/home/custom-api',
  PetPooja: '/home/petpooja',
};

function NavigationCard({ data, navigate, sendMessage }: { data: any; navigate: NavFn; sendMessage: SendFn }) {
  const route: string = data.navigateTo || '';
  const meta = NAV_PAGE_META[route] ?? DEFAULT_NAV_META;
  const Icon = meta.icon;
  const label = meta.title !== 'Page' ? meta.title : data.pageName || 'Page';

  // Render inline forms for integrations instead of navigating
  if (route === '/home/woocommerce') return <WooCommerceConnectCard navigate={navigate} />;
  if (route === '/home/business-accounts') return <BusinessAccountConnectCard navigate={navigate} />;
  if (route === '/home/shopify') return <ShopifyConnectCard navigate={navigate} />;
  if (route === '/home/razorpay') return <RazorpayConnectCard navigate={navigate} />;

  return (
    <div className={cn(
      "group relative overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]",
      "transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(0,0,0,0.12)]"
    )}>
      {/* Hover bg */}
      <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100", meta.bgGradient)} />

      <div className="relative z-10 p-4">
        {/* Icon + title row */}
        <div className="flex items-start gap-3">
          <div className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br shadow-md",
            "transition-transform duration-300 group-hover:scale-110",
            meta.gradient
          )}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h4 className="text-[14px] font-bold text-slate-800">{label}</h4>
            <p className="mt-0.5 text-[11.5px] leading-snug text-slate-500">{meta.description}</p>
          </div>
        </div>

        {/* Integration chips — clickable to open inline connect */}
        {meta.chips && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {meta.chips.map(chip => {
              const chipRoute = CHIP_ROUTES[chip];
              return (
                <button
                  key={chip}
                  type="button"
                  onClick={() => {
                    const inlineMessages: Record<string, string> = {
                      '/home/woocommerce': 'I want to connect my WooCommerce account',
                      '/home/shopify': 'I want to connect my Shopify store',
                      '/home/razorpay': 'I want to connect Razorpay',
                    };
                    const msg = inlineMessages[chipRoute];
                    if (msg) {
                      sendMessage(msg);
                    } else if (chipRoute) {
                      navigate(chipRoute);
                    }
                  }}
                  className="rounded-full border border-violet-200 bg-white/80 px-2.5 py-0.5 text-[10.5px] font-medium text-violet-700 shadow-sm transition-colors hover:border-violet-400 hover:bg-violet-50"
                >
                  {chip}
                </button>
              );
            })}
          </div>
        )}

        {/* CTA button */}
        <button
          type="button"
          onClick={() => navigate(route)}
          className={cn(
            "mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-[12.5px] font-semibold text-white shadow-sm",
            "bg-gradient-to-r transition-all duration-200 hover:opacity-90 hover:shadow-md active:scale-[0.98]",
            meta.gradient
          )}
        >
          Open {label}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── WooCommerce inline connect card ───────────────────────────────────────────
function WooCommerceConnectCard({ navigate }: { navigate: NavFn }) {
  const { selectedAccountId } = useBusinessAccountContext();
  const [form, setForm] = React.useState({ storeUrl: '', consumerKey: '', consumerSecret: '' });
  const [status, setStatus] = React.useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = React.useState('');
  const [storeName, setStoreName] = React.useState('');

  const handleConnect = async () => {
    if (!form.storeUrl || !form.consumerKey || !form.consumerSecret) {
      setErrorMsg('Please fill in all fields.');
      setStatus('error');
      return;
    }
    if (!selectedAccountId) {
      setErrorMsg('No business account selected.');
      setStatus('error');
      return;
    }
    setStatus('connecting');
    setErrorMsg('');
    try {
      const res = await woocommerceApi.connect({
        businessId: selectedAccountId,
        storeUrl: form.storeUrl,
        consumerKey: form.consumerKey,
        consumerSecret: form.consumerSecret,
      });
      setStoreName(res?.storeName || form.storeUrl);
      setStatus('success');
    } catch (err: any) {
      setErrorMsg(err?.message || 'Connection failed. Check your credentials.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 px-4 py-4 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 shadow-sm">
              <ShoppingBag className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="text-[14px] font-bold">WooCommerce Connected!</h4>
              <p className="text-[11px] text-white/75">{storeName}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-medium">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Store synced and ready
          </div>
          <button type="button" onClick={() => navigate('/home/woocommerce')}
            className="flex items-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:underline">
            Manage <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-500 to-purple-600 px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
            <ShoppingBag className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="text-[13.5px] font-bold text-white">Connect WooCommerce</h4>
            <p className="text-[10.5px] text-white/70">Sync your store products & orders</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-3 px-4 py-3.5">
        <div>
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
            Store URL
          </label>
          <input
            type="url"
            placeholder="https://yourstore.com"
            value={form.storeUrl}
            onChange={e => setForm(f => ({ ...f, storeUrl: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700 outline-none transition-all focus:border-violet-400 focus:bg-white focus:ring-2 focus:ring-violet-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
            Consumer Key
          </label>
          <input
            type="text"
            placeholder="ck_xxxxxxxxxxxxxxxx"
            value={form.consumerKey}
            onChange={e => setForm(f => ({ ...f, consumerKey: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700 outline-none transition-all focus:border-violet-400 focus:bg-white focus:ring-2 focus:ring-violet-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
            Consumer Secret
          </label>
          <input
            type="password"
            placeholder="cs_xxxxxxxxxxxxxxxx"
            value={form.consumerSecret}
            onChange={e => setForm(f => ({ ...f, consumerSecret: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700 outline-none transition-all focus:border-violet-400 focus:bg-white focus:ring-2 focus:ring-violet-100"
          />
        </div>

        {status === 'error' && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-600">{errorMsg}</p>
        )}

        <p className="text-[10.5px] text-slate-400">
          Find these in WooCommerce → Settings → Advanced → REST API
        </p>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={status === 'connecting'}
            onClick={handleConnect}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-[12.5px] font-semibold text-white shadow-sm",
              "bg-gradient-to-r from-violet-500 to-purple-600 transition-all hover:opacity-90 hover:shadow-md active:scale-[0.98]",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {status === 'connecting' ? (
              <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Connecting…</>
            ) : (
              <><Link2 className="h-3.5 w-3.5" /> Connect Store</>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/home/woocommerce')}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2.5 text-[12px] font-medium text-slate-500 transition-colors hover:border-violet-300 hover:text-violet-600"
          >
            Full page <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Business Account inline connect card ──────────────────────────────────────
function BusinessAccountConnectCard({ navigate }: { navigate: NavFn }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<'idle' | 'success'>('idle');
  const [accountName, setAccountName] = React.useState('');

  const handleSuccess = (accounts: any[]) => {
    const name = accounts?.[0]?.business_name || accounts?.[0]?.name || 'Your account';
    setAccountName(name);
    setStatus('success');
    queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });
  };

  if (status === 'success') {
    return (
      <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 px-4 py-4 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
              <CheckCircle2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="text-[14px] font-bold">Business Account Connected!</h4>
              <p className="text-[11px] text-white/75">{accountName}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-medium">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Ready to use
          </span>
          <button type="button" onClick={() => navigate('/home/business-accounts')}
            className="flex items-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:underline">
            Manage <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
      <div className="bg-gradient-to-br from-violet-600 to-indigo-600 px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="text-[13.5px] font-bold text-white">Connect Business Account</h4>
            <p className="text-[10.5px] text-white/70">WhatsApp Business API via Meta</p>
          </div>
        </div>
      </div>
      <div className="px-4 py-3.5">
        <WhatsAppEmbeddedSignup onSuccess={handleSuccess} onError={() => { }} />
      </div>
    </div>
  );
}

// ── Shopify inline connect card ────────────────────────────────────────────────
function ShopifyConnectCard({ navigate }: { navigate: NavFn }) {
  const { selectedAccountId } = useBusinessAccountContext();
  const [form, setForm] = React.useState({ shop: '', accessToken: '' });
  const [status, setStatus] = React.useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = React.useState('');
  const [storeName, setStoreName] = React.useState('');

  const handleConnect = async () => {
    if (!form.shop || !form.accessToken) { setErrorMsg('Please fill in all fields.'); setStatus('error'); return; }
    if (!selectedAccountId) { setErrorMsg('No business account selected.'); setStatus('error'); return; }
    setStatus('connecting'); setErrorMsg('');
    try {
      const res = await apiClient.post('/shopify/manual-install', {
        businessId: selectedAccountId,
        shop: form.shop,
        accessToken: form.accessToken,
      });
      setStoreName((res as any)?.storeName || form.shop);
      setStatus('success');
    } catch (err: any) {
      setErrorMsg(err?.message || 'Connection failed. Check your credentials.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
        <div className="bg-gradient-to-br from-green-400 to-emerald-500 px-4 py-4 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
              <ShoppingBag className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="text-[14px] font-bold">Shopify Connected!</h4>
              <p className="text-[11px] text-white/75">{storeName}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-medium">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Store synced and ready
          </span>
          <button type="button" onClick={() => navigate('/home/shopify')}
            className="flex items-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:underline">
            Manage <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
      <div className="bg-gradient-to-br from-green-400 to-emerald-500 px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
            <ShoppingBag className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="text-[13.5px] font-bold text-white">Connect Shopify</h4>
            <p className="text-[10.5px] text-white/70">Sync your store products & orders</p>
          </div>
        </div>
      </div>
      <div className="space-y-3 px-4 py-3.5">
        <div>
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">Shop Domain</label>
          <input type="text" placeholder="yourstore.myshopify.com" value={form.shop}
            onChange={e => setForm(f => ({ ...f, shop: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700 outline-none transition-all focus:border-green-400 focus:bg-white focus:ring-2 focus:ring-green-100" />
        </div>
        <div>
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">Admin API Access Token</label>
          <input type="password" placeholder="shpat_xxxxxxxxxxxxxxxx" value={form.accessToken}
            onChange={e => setForm(f => ({ ...f, accessToken: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700 outline-none transition-all focus:border-green-400 focus:bg-white focus:ring-2 focus:ring-green-100" />
        </div>
        {status === 'error' && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-600">{errorMsg}</p>
        )}
        <p className="text-[10.5px] text-slate-400">Find this in Shopify Admin → Settings → Apps → Develop apps</p>
        <div className="flex items-center gap-2">
          <button type="button" disabled={status === 'connecting'} onClick={handleConnect}
            className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-[12.5px] font-semibold text-white shadow-sm",
              "bg-gradient-to-r from-green-400 to-emerald-500 transition-all hover:opacity-90 hover:shadow-md active:scale-[0.98]",
              "disabled:opacity-50 disabled:cursor-not-allowed")}>
            {status === 'connecting' ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Connecting…</> : <><Link2 className="h-3.5 w-3.5" /> Connect Store</>}
          </button>
          <button type="button" onClick={() => navigate('/home/shopify')}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2.5 text-[12px] font-medium text-slate-500 transition-colors hover:border-green-300 hover:text-green-600">
            Full page <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Razorpay inline connect card ───────────────────────────────────────────────
function RazorpayConnectCard({ navigate }: { navigate: NavFn }) {
  const { selectedAccountId } = useBusinessAccountContext();
  const [form, setForm] = React.useState({ keyId: '', keySecret: '' });
  const [status, setStatus] = React.useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = React.useState('');

  const handleConnect = async () => {
    if (!form.keyId || !form.keySecret) { setErrorMsg('Please fill in both fields.'); setStatus('error'); return; }
    if (!selectedAccountId) { setErrorMsg('No business account selected.'); setStatus('error'); return; }
    setStatus('connecting'); setErrorMsg('');
    try {
      await razorpayApi.connect({ businessId: selectedAccountId, keyId: form.keyId, keySecret: form.keySecret });
      setStatus('success');
    } catch (err: any) {
      setErrorMsg(err?.message || 'Connection failed. Check your API keys.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
        <div className="bg-gradient-to-br from-sky-500 to-blue-600 px-4 py-4 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
              <CheckCircle2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="text-[14px] font-bold">Razorpay Connected!</h4>
              <p className="text-[11px] text-white/75">Ready to process payments</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-medium">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Payment gateway active
          </span>
          <button type="button" onClick={() => navigate('/home/razorpay')}
            className="flex items-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:underline">
            Manage <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border-0 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
      <div className="bg-gradient-to-br from-sky-500 to-blue-600 px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
            <CreditCard className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="text-[13.5px] font-bold text-white">Connect Razorpay</h4>
            <p className="text-[10.5px] text-white/70">Process payments via WhatsApp</p>
          </div>
        </div>
      </div>
      <div className="space-y-3 px-4 py-3.5">
        <div>
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">Key ID</label>
          <input type="text" placeholder="rzp_live_xxxxxxxxxxxxxxxx" value={form.keyId}
            onChange={e => setForm(f => ({ ...f, keyId: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700 outline-none transition-all focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100" />
        </div>
        <div>
          <label className="mb-1 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">Key Secret</label>
          <input type="password" placeholder="••••••••••••••••" value={form.keySecret}
            onChange={e => setForm(f => ({ ...f, keySecret: e.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-700 outline-none transition-all focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100" />
        </div>
        {status === 'error' && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-600">{errorMsg}</p>
        )}
        <p className="text-[10.5px] text-slate-400">Find these in Razorpay Dashboard → Settings → API Keys</p>
        <div className="flex items-center gap-2">
          <button type="button" disabled={status === 'connecting'} onClick={handleConnect}
            className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-[12.5px] font-semibold text-white shadow-sm",
              "bg-gradient-to-r from-sky-500 to-blue-600 transition-all hover:opacity-90 hover:shadow-md active:scale-[0.98]",
              "disabled:opacity-50 disabled:cursor-not-allowed")}>
            {status === 'connecting' ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Connecting…</> : <><Link2 className="h-3.5 w-3.5" /> Connect Razorpay</>}
          </button>
          <button type="button" onClick={() => navigate('/home/razorpay')}
            className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2.5 text-[12px] font-medium text-slate-500 transition-colors hover:border-sky-300 hover:text-sky-600">
            Full page <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create automation flow card ───────────────────────────────────────────────
function CreateFlowCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const flowId = data.flowId || data.id;
  const navigatePath = data.navigateTo || (flowId ? `/home/automation/flows/${flowId}` : '/home/automation/flows');

  return (
    <div
      onClick={() => navigate(navigatePath)}
      className="group overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)] cursor-pointer hover:border-violet-300 transition-all duration-200"
    >
      <div className="border-b border-violet-100 bg-gradient-to-r from-violet-50 to-purple-50/40 px-3.5 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-violet-600 group-hover:scale-105 transition-transform">
            <Zap className="h-3.5 w-3.5 text-white" />
          </div>
          <div>
            <p className="text-[12px] font-bold text-violet-700">Draft Flow Created</p>
            <p className="text-[10px] text-violet-500 font-mono">{data.name}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(navigatePath); }}
          className="rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-200 transition-colors"
        >
          Open Builder →
        </button>
      </div>
      <div className="p-3.5 space-y-2">
        <p className="text-[12px] text-slate-600 leading-snug">
          {data.message || 'The draft is ready. Keep describing the booking flow here and I’ll build it step by step.'}
        </p>
        <p className="text-[11px] text-violet-500">If anything is unclear, I’ll ask the next question before adding it.</p>
      </div>
    </div>
  );
}

// ── Add flow node card (live canvas injection) ─────────────────────────────────
const NODE_TYPE_META: Record<string, { icon: string; color: string }> = {
  starting_step: { icon: '▶️', color: 'bg-green-50 border-green-200 text-green-700' },
  send_message: { icon: '💬', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  send_image: { icon: '🖼️', color: 'bg-pink-50 border-pink-200 text-pink-700' },
  send_list: { icon: '📋', color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  send_template: { icon: '📄', color: 'bg-violet-50 border-violet-200 text-violet-700' },
  condition: { icon: '🔀', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  wait: { icon: '⏱️', color: 'bg-slate-50 border-slate-200 text-slate-700' },
  user_input: { icon: '⌨️', color: 'bg-teal-50 border-teal-200 text-teal-700' },
  tag: { icon: '🏷️', color: 'bg-green-50 border-green-200 text-green-700' },
  assign_agent: { icon: '👤', color: 'bg-cyan-50 border-cyan-200 text-cyan-700' },
  send_email: { icon: '✉️', color: 'bg-orange-50 border-orange-200 text-orange-700' },
  ai_agent: { icon: '🤖', color: 'bg-purple-50 border-purple-200 text-purple-700' },
  webhook: { icon: '🔗', color: 'bg-rose-50 border-rose-200 text-rose-700' },
  collect_payment: { icon: '💳', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  end: { icon: '🏁', color: 'bg-red-50 border-red-200 text-red-700' },
};

function AddNodeCard({ data }: { data: any }) {
  const dispatched = React.useRef(false);
  React.useEffect(() => {
    if (dispatched.current) return;
    dispatched.current = true;
    const detail = { nodeType: data.nodeType, label: data.label, config: data.config || {}, nodeId: data.nodeId };
    // Dispatch immediately AND after a longer delay so FlowBuilder has time to mount after navigation
    window.dispatchEvent(new CustomEvent('aria:add-node', { detail }));
    const t1 = setTimeout(() => window.dispatchEvent(new CustomEvent('aria:add-node', { detail: { ...detail, nodeId: detail.nodeId } })), 1500);
    const t2 = setTimeout(() => window.dispatchEvent(new CustomEvent('aria:add-node', { detail: { ...detail, nodeId: detail.nodeId } })), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [data.nodeType, data.label, data.config, data.nodeId]);

  const meta = NODE_TYPE_META[data.nodeType] ?? { icon: '⬡', color: 'bg-slate-50 border-slate-200 text-slate-700' };
  return (
    <div className={cn("flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5", meta.color)}>
      <span className="text-[18px] leading-none">{meta.icon}</span>
      <div>
        <p className="text-[12px] font-semibold">{data.label} added to canvas ✓</p>
        {data.config && Object.keys(data.config).length > 0 && (
          <p className="text-[10.5px] opacity-70 mt-0.5 truncate max-w-[200px]">
            {Object.entries(data.config).map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Update node card (live canvas config update) ──────────────────────────────
function UpdateNodeCard({ data }: { data: any }) {
  const dispatched = React.useRef(false);
  React.useEffect(() => {
    if (dispatched.current) return;
    dispatched.current = true;
    // Backend safety net: if update_flow_node was redirected to add_node (type mismatch), dispatch add event
    if (data.action === 'add_node') {
      const detail = { nodeType: data.nodeType, label: data.label, config: data.config || {}, nodeId: data.nodeId };
      window.dispatchEvent(new CustomEvent('aria:add-node', { detail }));
      const t1 = setTimeout(() => window.dispatchEvent(new CustomEvent('aria:add-node', { detail })), 1500);
      const t2 = setTimeout(() => window.dispatchEvent(new CustomEvent('aria:add-node', { detail })), 3000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    const detail = { nodeId: data.nodeId, label: data.label, config: data.config || {} };
    window.dispatchEvent(new CustomEvent('aria:update-node', { detail }));
    const t1 = setTimeout(() => window.dispatchEvent(new CustomEvent('aria:update-node', { detail })), 1500);
    const t2 = setTimeout(() => window.dispatchEvent(new CustomEvent('aria:update-node', { detail })), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [data.nodeId, data.label, data.config, data.action]);

  return (
    <div className="flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 bg-indigo-50 border-indigo-200 text-indigo-700">
      <span className="text-[18px] leading-none">✏️</span>
      <div>
        <p className="text-[12px] font-semibold">Node updated ✓</p>
        {data.config && Object.keys(data.config).length > 0 && (
          <p className="text-[10.5px] opacity-70 mt-0.5 truncate max-w-[200px]">
            {Object.entries(data.config).map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}

function ConnectNodesCard({ data }: { data: any }) {
  const dispatched = React.useRef(false);
  React.useEffect(() => {
    if (dispatched.current) return;
    dispatched.current = true;
    const detail = {
      sourceNodeId: data.sourceNodeId,
      targetNodeId: data.targetNodeId,
      connectionMode: data.connectionMode || 'default',
    };
    window.dispatchEvent(new CustomEvent('aria:connect-nodes', { detail }));
    const t = setTimeout(() => window.dispatchEvent(new CustomEvent('aria:connect-nodes', { detail })), 1200);
    return () => clearTimeout(t);
  }, [data.sourceNodeId, data.targetNodeId, data.connectionMode]);

  return (
    <div className="flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 bg-emerald-50 border-emerald-200 text-emerald-700">
      <span className="text-[18px] leading-none">🔗</span>
      <div>
        <p className="text-[12px] font-semibold">Nodes connected ✓</p>
        <p className="text-[10.5px] opacity-70 mt-0.5 truncate max-w-[220px]">
          {data.connectionMode === 'all_list_rows' ? 'All list options connected to the target node' : 'Connection added'}
        </p>
      </div>
    </div>
  );
}

function SaveFlowCard({ data }: { data: any }) {
  const dispatched = React.useRef(false);
  React.useEffect(() => {
    if (dispatched.current) return;
    dispatched.current = true;
    window.dispatchEvent(new CustomEvent('aria:save-flow', {
      detail: { name: data.name || '' },
    }));
  }, [data.name]);

  return (
    <div className="flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 bg-emerald-50 border-emerald-200 text-emerald-700">
      <span className="text-[18px] leading-none">💾</span>
      <div>
        <p className="text-[12px] font-semibold">Saving flow ✓</p>
        <p className="text-[10.5px] opacity-70 mt-0.5 truncate max-w-[220px]">
          {data.name ? `Name: ${data.name}` : 'Saving the current flow'}
        </p>
      </div>
    </div>
  );
}

// ── Onboarding guide card ─────────────────────────────────────────────────────
const REQ_ICON: Record<string, React.ElementType> = {
  phone: Phone,
  verified: ShieldCheck,
  card: CreditCard,
};

function OnboardingGuideCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const [showSignup, setShowSignup] = React.useState(false);
  const steps: any[] = data.steps || [];
  const requirements: any[] = data.requirements || [];
  const primaryAction: { label: string; page?: string; action?: string } | undefined = data.primaryAction;
  const hasConnectedAccount: boolean = data.hasConnectedAccount ?? false;
  const connectedAccounts: any[] = data.connectedAccounts || [];

  const triggerSignup = () => setShowSignup(true);
  const handleCtaClick = () => {
    if (hasConnectedAccount) {
      navigate('/home/business-accounts');
    } else if (primaryAction?.action === 'embedded_signup') {
      triggerSignup();
    } else if (primaryAction?.page) {
      navigate(primaryAction.page);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      {/* Header */}
      <div className={cn(
        "border-b px-3.5 py-2.5",
        hasConnectedAccount
          ? "border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50/40"
          : "border-violet-100 bg-gradient-to-r from-violet-50 to-purple-50/40"
      )}>
        <div className="flex items-center gap-2">
          <div className={cn(
            "flex h-6 w-6 items-center justify-center rounded-md",
            hasConnectedAccount ? "bg-emerald-500" : "bg-violet-600"
          )}>
            <CheckCircle2 className="h-3.5 w-3.5 text-white" />
          </div>
          <div>
            <p className={cn("text-[12px] font-bold", hasConnectedAccount ? "text-emerald-700" : "text-violet-700")}>
              {data.title}
            </p>
            {data.subtitle && (
              <p className={cn("text-[10px]", hasConnectedAccount ? "text-emerald-500" : "text-violet-500")}>
                {data.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-3.5 space-y-3.5">

        {/* Already connected banner */}
        {hasConnectedAccount && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
              <p className="text-[12px] font-semibold text-emerald-700">Account already connected!</p>
            </div>
            <div className="space-y-1">
              {connectedAccounts.map((acc: any, i: number) => (
                <div key={i} className="flex items-center justify-between">
                  <p className="text-[11px] text-emerald-600">{acc.name || acc.phone}</p>
                  {acc.phone && acc.name !== acc.phone && (
                    <p className="text-[10px] text-emerald-400">{acc.phone}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Steps — only show if no connected account */}
        {!hasConnectedAccount && (
          <div className="space-y-2">
            {steps.map((step: any) => (
              <div key={step.number} className="flex items-start gap-2.5">
                <div className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5",
                  step.action === 'embedded_signup' ? "bg-violet-600" : "bg-violet-100"
                )}>
                  <span className={cn("text-[9px] font-bold", step.action === 'embedded_signup' ? "text-white" : "text-violet-600")}>
                    {step.number}
                  </span>
                </div>
                <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
                  <p className="text-[12px] leading-snug text-slate-700">{step.text}</p>
                  {step.action === 'embedded_signup' && !showSignup && (
                    <button
                      type="button"
                      onClick={triggerSignup}
                      className="shrink-0 rounded-md bg-violet-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-violet-700 transition-colors"
                    >
                      {step.actionLabel ?? 'Launch'} →
                    </button>
                  )}
                  {step.navigateTo && (
                    <button
                      type="button"
                      onClick={() => navigate(step.navigateTo)}
                      className="shrink-0 rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-200 transition-colors"
                    >
                      {step.actionLabel ?? 'Open'} →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inline embedded signup — only when no account and triggered */}
        {!hasConnectedAccount && showSignup && (
          <div className="rounded-xl border border-violet-200 overflow-hidden">
            <div className="flex items-center justify-between bg-violet-50 px-3 py-2 border-b border-violet-100">
              <span className="text-[10.5px] font-bold uppercase tracking-widest text-violet-600">Meta Embedded Signup</span>
              <button
                type="button"
                onClick={() => setShowSignup(false)}
                className="text-[10px] text-violet-400 hover:text-violet-600 transition-colors"
              >
                ✕ close
              </button>
            </div>
            <div className="p-2">
              <WhatsAppEmbeddedSignup
                onSuccess={() => setShowSignup(false)}
                onError={() => { }}
                autoActivateOnboarding={true}
              />
            </div>
          </div>
        )}

        {/* Note */}
        {data.note && (
          <p className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-[11px] text-amber-700 leading-snug">
            💡 {data.note}
          </p>
        )}

        {/* Requirements — only when not connected */}
        {!hasConnectedAccount && requirements.length > 0 && (
          <div>
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Requirements</p>
            <div className="space-y-1.5">
              {requirements.map((req: any, i: number) => {
                const Icon = REQ_ICON[req.icon] ?? CheckCircle2;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                    <p className="text-[11.5px] text-slate-600">{req.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Primary CTA */}
        {!showSignup && (
          <button
            type="button"
            onClick={handleCtaClick}
            className={cn(
              "flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-[12.5px] font-semibold text-white shadow-sm transition-all hover:opacity-90 hover:shadow-md active:scale-[0.98]",
              hasConnectedAccount
                ? "bg-gradient-to-r from-emerald-500 to-teal-600"
                : "bg-gradient-to-r from-violet-500 to-purple-600"
            )}
          >
            {hasConnectedAccount ? 'View Business Accounts' : (primaryAction?.label ?? 'Get Started')}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Bookings card ─────────────────────────────────────────────────────────────
function BookingsCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const bookings: any[] = data.bookings || [];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label={`${data.type === 'past' ? 'Past' : 'Upcoming'} Bookings`} count={data.count} />
      {bookings.length === 0 ? (
        <div className="px-4 py-5 text-center">
          <Calendar className="mx-auto h-8 w-8 text-violet-200 mb-2" />
          <p className="text-[12px] text-slate-400">No bookings found</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {bookings.slice(0, 8).map((b: any, i: number) => (
            <div key={i} className="flex items-center justify-between px-3.5 py-2.5 hover:bg-violet-50/40 transition-colors">
              <div className="min-w-0">
                <p className="truncate text-[12.5px] font-semibold text-slate-700">{b.title || b.eventTypeName || 'Booking'}</p>
                <p className="text-[11px] text-slate-400">
                  {b.attendee?.name || b.guestName || '—'} · {b.startTime ? new Date(b.startTime).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </p>
              </div>
              <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                b.status === 'confirmed' ? "bg-emerald-50 text-emerald-600" :
                  b.status === 'cancelled' ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"
              )}>{b.status || 'pending'}</span>
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-violet-50 px-3.5 py-2">
        <button type="button" onClick={() => navigate('/home/scheduling')}
          className="flex w-full items-center justify-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:text-violet-800 transition-colors">
          View all in Scheduling <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Event types card ──────────────────────────────────────────────────────────
function EventTypesCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const eventTypes: any[] = data.eventTypes || [];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Event Types / Services" count={data.count} />
      {eventTypes.length === 0 ? (
        <div className="px-4 py-5 text-center">
          <p className="text-[12px] text-slate-400">No event types set up yet</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {eventTypes.map((et: any, i: number) => (
            <div key={i} className="flex items-center justify-between px-3.5 py-2.5 hover:bg-violet-50/40 transition-colors">
              <div>
                <p className="text-[12.5px] font-semibold text-slate-700">{et.title || et.name}</p>
                <p className="text-[11px] text-slate-400">{et.duration ? `${et.duration} min` : ''} {et.price ? `· ₹${et.price}` : ''}</p>
              </div>
              <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", et.isActive !== false ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500")}>
                {et.isActive !== false ? 'active' : 'inactive'}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-violet-50 px-3.5 py-2">
        <button type="button" onClick={() => navigate('/home/scheduling')}
          className="flex w-full items-center justify-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:text-violet-800 transition-colors">
          Manage in Scheduling <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Social posts card ─────────────────────────────────────────────────────────
const PLATFORM_STYLE: Record<string, { label: string; bg: string; text: string }> = {
  instagram: { label: 'IG', bg: 'bg-pink-50', text: 'text-pink-600' },
  facebook: { label: 'FB', bg: 'bg-blue-50', text: 'text-blue-600' },
  linkedin: { label: 'LI', bg: 'bg-sky-50', text: 'text-sky-700' },
  whatsapp: { label: 'WA', bg: 'bg-emerald-50', text: 'text-emerald-600' },
  shopify: { label: 'SP', bg: 'bg-green-50', text: 'text-green-600' },
  woocommerce: { label: 'WC', bg: 'bg-purple-50', text: 'text-purple-600' },
};

function SocialPostsCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const posts: any[] = data.posts || [];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Social Posts" count={`${data.count} found`} />
      {posts.length === 0 ? (
        <div className="px-4 py-5 text-center">
          <ImageIcon className="mx-auto h-8 w-8 text-violet-200 mb-2" />
          <p className="text-[12px] text-slate-400">No posts yet</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {posts.slice(0, 6).map((p: any, i: number) => {
            const platform = (p.platform || '').toLowerCase();
            const ps = PLATFORM_STYLE[platform] ?? { label: platform.slice(0, 2).toUpperCase(), bg: 'bg-slate-50', text: 'text-slate-600' };
            return (
              <div key={i} className="flex items-start gap-2.5 px-3.5 py-2.5 hover:bg-violet-50/40 transition-colors">
                <span className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase mt-0.5", ps.bg, ps.text)}>{ps.label}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] text-slate-700">{p.caption || p.content || '—'}</p>
                  <p className="text-[10.5px] text-slate-400">{p.status} · {p.publishedAt ? new Date(p.publishedAt).toLocaleDateString() : (p.scheduledAt ? `Scheduled ${new Date(p.scheduledAt).toLocaleDateString()}` : '—')}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="border-t border-violet-50 px-3.5 py-2">
        <button type="button" onClick={() => navigate('/home/social-media/posts')}
          className="flex w-full items-center justify-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:text-violet-800 transition-colors">
          View all posts <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Social accounts card ──────────────────────────────────────────────────────
function AiImageCard({ data }: { data: any }) {
  const imageUrl = data?.image_url;
  if (!imageUrl) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="AI Generated Image" />
      <div className="p-3">
        <img
          src={imageUrl}
          alt="AI generated image"
          className="w-full rounded-lg object-cover"
          style={{ maxHeight: 400 }}
        />
        {data.width && data.height && (
          <p className="mt-2 text-[10.5px] text-gray-400">{data.width} x {data.height}</p>
        )}
      </div>
    </div>
  );
}

function SocialAccountsCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const accounts = Array.isArray(data.accounts) ? data.accounts : [];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Connected Social Accounts" count={data.count} />
      {accounts.length === 0 ? (
        <div className="px-4 py-5 text-center">
          <Share2 className="mx-auto h-8 w-8 text-violet-200 mb-2" />
          <p className="text-[12px] text-slate-400">No social accounts connected yet</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {accounts.map((a: any, i: number) => {
            const platform = (a.platform || '').toLowerCase();
            const ps = PLATFORM_STYLE[platform] ?? { label: platform.slice(0, 2).toUpperCase(), bg: 'bg-slate-50', text: 'text-slate-600' };
            return (
              <div key={i} className="flex items-center gap-2.5 px-3.5 py-2.5">
                <span className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase", ps.bg, ps.text)}>{ps.label}</span>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-[12.5px] font-semibold text-slate-700">{a.name || a.username || a.accountName}</p>
                  {a.username && a.name && <p className="text-[11px] text-slate-400">@{a.username}</p>}
                </div>
                <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", a.isConnected !== false ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500")}>
                  {a.isConnected !== false ? 'connected' : 'disconnected'}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <div className="border-t border-violet-50 px-3.5 py-2">
        <button type="button" onClick={() => navigate('/home/integrations')}
          className="flex w-full items-center justify-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:text-violet-800 transition-colors">
          Manage connections <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Commerce stats card ───────────────────────────────────────────────────────
function CommerceStatsCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const statsSource = data?.data || data || {};
  const totalProducts = statsSource.totalProducts ?? statsSource.total_products ?? statsSource.products;
  const totalOrders = statsSource.totalOrders ?? statsSource.total_orders ?? statsSource.orders;
  const totalRevenue = statsSource.totalRevenue ?? statsSource.total_revenue ?? statsSource.revenue;
  const pendingOrders = statsSource.pendingOrders ?? statsSource.pending_orders ?? statsSource.pending;

  const stats = [
    { label: 'Products', value: totalProducts ?? '—', icon: Layers },
    { label: 'Orders', value: totalOrders ?? '—', icon: ShoppingCart },
    { label: 'Revenue', value: totalRevenue != null ? `₹${Number(totalRevenue).toLocaleString()}` : '—', icon: TrendingUp },
    { label: 'Pending', value: pendingOrders ?? '—', icon: Tag },
  ];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Commerce Overview" />
      <div className="grid grid-cols-2 gap-px bg-slate-100 border-b border-slate-100">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="flex flex-col items-center justify-center bg-white py-3.5 px-2">
            <Icon className="h-4 w-4 text-violet-400 mb-1" />
            <p className="text-[16px] font-bold text-slate-800">{value}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
          </div>
        ))}
      </div>
      <div className="px-3.5 py-2">
        <button type="button" onClick={() => navigate('/home/commerce/dashboard')}
          className="flex w-full items-center justify-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:text-violet-800 transition-colors">
          Open Commerce <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Products card ─────────────────────────────────────────────────────────────
function ProductsCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const products: any[] = data.products || [];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Products" count={`${data.count} found`} />
      {products.length === 0 ? (
        <div className="px-4 py-5 text-center">
          <Layers className="mx-auto h-8 w-8 text-violet-200 mb-2" />
          <p className="text-[12px] text-slate-400">No products found</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {products.slice(0, 8).map((p: any, i: number) => (
            <div key={i} className="flex items-center justify-between px-3.5 py-2.5 hover:bg-violet-50/40 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-semibold text-slate-700">{p.name || p.title}</p>
                {p.description && <p className="truncate text-[11px] text-slate-400">{p.description}</p>}
              </div>
              {(p.price != null) && (
                <span className="shrink-0 ml-2 text-[12px] font-bold text-violet-600">₹{Number(p.price).toLocaleString()}</span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-violet-50 px-3.5 py-2">
        <button type="button" onClick={() => navigate('/home/commerce/products')}
          className="flex w-full items-center justify-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:text-violet-800 transition-colors">
          View all products <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Tags card ─────────────────────────────────────────────────────────────────
const TAG_COLORS = [
  'border-violet-200 bg-violet-50 text-violet-700',
  'border-blue-200 bg-blue-50 text-blue-700',
  'border-emerald-200 bg-emerald-50 text-emerald-700',
  'border-amber-200 bg-amber-50 text-amber-700',
  'border-rose-200 bg-rose-50 text-rose-700',
  'border-indigo-200 bg-indigo-50 text-indigo-700',
  'border-teal-200 bg-teal-50 text-teal-700',
];

function TagsCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const tags: any[] = data.tags || [];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Tags" count={`${data.count} found`} />
      <div className="p-3.5">
        {tags.length === 0 ? (
          <div className="py-4 text-center">
            <p className="text-[12px] text-slate-400">No tags yet</p>
            <button type="button" onClick={() => sendMessage('Create a new tag')}
              className="mt-2 text-[11px] font-medium text-violet-500 hover:underline">
              + Create your first tag
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => sendMessage(`Show me contacts with the "${t.name}" tag`)}
                className={cn(
                  'group flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-medium transition-all hover:shadow-sm',
                  TAG_COLORS[i % TAG_COLORS.length]
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                {t.name}
                {t.count > 0 && (
                  <span className="rounded-full bg-black/8 px-1.5 py-0 text-[10px] font-semibold">
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
          <button type="button" onClick={() => sendMessage('Create a new tag')}
            className="rounded-lg border border-dashed border-violet-200 px-3 py-1.5 text-[11px] font-medium text-violet-500 transition-colors hover:border-violet-400 hover:bg-violet-50">
            + New tag
          </button>
          <button type="button" onClick={() => sendMessage('Assign a tag to contacts')}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-violet-300 hover:text-violet-600">
            Assign tags →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Team members card ─────────────────────────────────────────────────────────
const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-violet-100 text-violet-700',
  manager: 'bg-blue-100 text-blue-700',
  agent: 'bg-emerald-100 text-emerald-700',
  owner: 'bg-amber-100 text-amber-700',
};

function TeamMembersCard({ data, sendMessage }: { data: any; sendMessage: SendFn }) {
  const members: any[] = data.members || [];
  const filterLabel = data.filter?.role ? ` (${data.filter.role.toLowerCase()}s)` : '';
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label={`Team Members${filterLabel}`} count={`${data.count} found`} />
      <div className="divide-y divide-slate-100/80">
        {members.map((m, i) => {
          const roleKey = (m.role || '').toLowerCase();
          const roleColor = ROLE_COLORS[roleKey] || 'bg-slate-100 text-slate-600';
          const isActive = m.status !== 'inactive' && m.status !== 'deactivated';
          return (
            <div key={i} className="group flex items-center gap-2.5 px-3.5 py-2.5 transition-colors hover:bg-violet-50/50">
              <div className="relative shrink-0">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-100 to-violet-200 text-[11px] font-bold text-violet-700">
                  {(m.name || '?')[0].toUpperCase()}
                </div>
                <span className={cn('absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white', isActive ? 'bg-emerald-400' : 'bg-slate-300')} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-medium text-slate-700">{m.name || 'Unknown'}</p>
                {m.email && <p className="truncate text-[11px] text-slate-400">{m.email}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button type="button"
                  onClick={() => sendMessage(`Show details for team member ${m.name}`)}
                  className="hidden rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 transition-colors hover:bg-violet-200 group-hover:block">
                  Details →
                </button>
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize group-hover:hidden', roleColor)}>
                  {m.role || 'Agent'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Profile card ──────────────────────────────────────────────────────────────
function ProfileCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const name = data.name || data.full_name;
  const roleKey = (data.role || '').toLowerCase();
  const roleColor = ROLE_COLORS[roleKey] || 'bg-slate-100 text-slate-600';
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="My Profile" />
      <div className="p-4 flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 text-[15px] font-bold text-emerald-700 overflow-hidden">
          {data.avatar_url
            ? <img src={data.avatar_url} alt={name} className="w-full h-full object-cover" />
            : (name ? name[0].toUpperCase() : <User className="w-5 h-5" />)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-slate-800">{name || '—'}</p>
          {data.email && <p className="text-[11.5px] text-slate-400 mt-0.5">{data.email}</p>}
          {data.role && (
            <span className={cn('mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize', roleColor)}>
              {data.role}
            </span>
          )}
        </div>
      </div>
      {data.timezone && (
        <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-2 text-[11.5px] text-slate-400">
          <Globe className="w-3.5 h-3.5" />
          <span>{data.timezone}</span>
        </div>
      )}
      <div className="border-t border-slate-100 p-2.5 flex gap-2">
        <button type="button"
          onClick={() => navigate('/home/settings/profile')}
          className="flex-1 rounded-lg py-2 text-[11.5px] font-medium text-violet-600 transition-colors hover:bg-violet-50">
          Edit Profile →
        </button>
        <button type="button"
          onClick={() => navigate('/home/settings')}
          className="flex-1 rounded-lg py-2 text-[11.5px] font-medium text-slate-500 transition-colors hover:bg-slate-50">
          Settings →
        </button>
      </div>
    </div>
  );
}

// ── Analytics card ────────────────────────────────────────────────────────────
function AnalyticsCard({ data }: { data: any }) {
  const a = data.analytics || {};
  const bd = a.messageBreakdown || {};
  const type = data.type || 'overview';
  const period = data.period || 'all';

  const periodLabel: Record<string, string> = { today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', '1y': 'Last year', all: 'All time' };

  const pick = (...keys: string[]) => { for (const k of keys) if (a[k] !== undefined) return a[k]; return undefined; };
  const fmtNum = (v: any) => {
    if (typeof v !== 'number') return v ?? '—';
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return v.toLocaleString();
  };
  const pct = (v: any) => v !== undefined && v !== null ? `${Number(v).toFixed(1)}%` : '—';

  // WeNext-style KPI tile — white bg, coloured icon pill, dark number, grey label
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };
  const KpiTile = ({ label, value, Icon, iconColor }: { label: string; value: string | number; Icon: React.ElementType; iconColor: string }) => (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 hover:shadow-sm transition-shadow">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: hexToRgba(iconColor, 0.12) }}>
        <Icon className="h-4 w-4" style={{ color: iconColor }} />
      </div>
      <p className="text-[20px] font-bold text-[#0F172A] leading-none tracking-tight">{value}</p>
      <p className="text-[10.5px] text-[#64748B] mt-1.5 font-medium">{label}</p>
    </div>
  );

  // ── Overview / Messages ──
  if (type === 'overview' || type === 'messages') {
    const totalMsgs = pick('totalMessages', 'total_messages') ?? 0;
    const activeChats = pick('activeConversations', 'active_conversations') ?? 0;
    const delRate = pick('deliveryRate', 'delivery_rate');
    const rdRate = pick('readRate', 'read_rate');
    const sent = bd.totalSent ?? bd.sent ?? pick('messagesSent', 'messages_sent') ?? 0;
    const delivered = bd.totalDelivered ?? bd.delivered ?? pick('messagesDelivered', 'messages_delivered') ?? 0;
    const read = bd.totalRead ?? bd.read ?? pick('messagesRead', 'messages_read') ?? 0;
    const failed = bd.totalFailed ?? bd.failed ?? pick('messagesFailed', 'messages_failed') ?? 0;
    const received = bd.totalReceived ?? bd.received ?? pick('totalReceived', 'messages_received') ?? 0;

    const breakdown = [
      { label: 'Sent', value: sent, bar: '#22C55E' },
      { label: 'Delivered', value: delivered, bar: '#10B981' },
      { label: 'Read', value: read, bar: '#F59E0B' },
      { label: 'Failed', value: failed, bar: '#EF4444' },
      { label: 'Received', value: received, bar: '#8B5CF6' },
    ];
    const maxBreakdown = Math.max(...breakdown.map(b => Number(b.value) || 0), 1);

    return (
      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-[#F8F9FB] shadow-sm">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E2E8F0] bg-white">
          <BarChart3 className="h-3.5 w-3.5 text-violet-500" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-violet-500">Analytics</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">Overview</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">{periodLabel[period] || period}</span>
        </div>
        {/* KPI grid — 2 cols */}
        <div className="grid grid-cols-2 gap-2 p-2.5">
          <KpiTile label="Total Messages" value={fmtNum(totalMsgs)} Icon={MessageSquare} iconColor="#22C55E" />
          <KpiTile label="Active Chats" value={fmtNum(activeChats)} Icon={MessageCircle} iconColor="#8B5CF6" />
          <KpiTile label="Delivery Rate" value={pct(delRate)} Icon={CheckCircle2} iconColor="#10B981" />
          <KpiTile label="Read Rate" value={pct(rdRate)} Icon={Eye} iconColor="#F59E0B" />
        </div>
        {/* Message breakdown */}
        <div className="mx-2.5 mb-2.5 bg-white rounded-xl border border-[#E2E8F0] px-3 py-2.5">
          <p className="text-[9.5px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">Message Breakdown</p>
          <div className="space-y-2">
            {breakdown.map(b => (
              <div key={b.label} className="flex items-center gap-2">
                <span className="w-[58px] shrink-0 text-[10.5px] text-[#64748B] font-medium">{b.label}</span>
                <div className="flex-1 h-[6px] rounded-full bg-[#F1F5F9] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.round((Number(b.value) / maxBreakdown) * 100)}%`, backgroundColor: b.bar }} />
                </div>
                <span className="w-[34px] text-right text-[10.5px] font-bold text-[#0F172A]">{fmtNum(b.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Conversations ──
  if (type === 'conversations') {
    const total = pick('totalConversations', 'total_conversations') ?? 0;
    const active = pick('activeConversations', 'active_conversations') ?? 0;
    const closed = pick('closedConversations', 'closed_conversations') ?? 0;
    const avgMsgs = pick('avgMessagesPerConversation', 'avg_messages');
    const avgResp = pick('avgResponseTime', 'avg_response_time');

    return (
      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-[#F8F9FB] shadow-sm">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E2E8F0] bg-white">
          <BarChart3 className="h-3.5 w-3.5 text-violet-500" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-violet-500">Analytics</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">Conversations</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">{periodLabel[period] || period}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 p-2.5">
          <KpiTile label="Total" value={fmtNum(total)} Icon={MessageSquare} iconColor="#8B5CF6" />
          <KpiTile label="Active" value={fmtNum(active)} Icon={MessageCircle} iconColor="#10B981" />
          <KpiTile label="Closed" value={fmtNum(closed)} Icon={CheckCircle2} iconColor="#64748B" />
          <KpiTile label="Avg Msgs" value={avgMsgs != null ? Number(avgMsgs).toFixed(1) : '—'} Icon={BarChart3} iconColor="#22C55E" />
        </div>
        {avgResp != null && (
          <div className="mx-2.5 mb-2.5 bg-white rounded-xl border border-[#E2E8F0] px-3 py-2 flex items-center justify-between">
            <span className="text-[11px] text-[#64748B] font-medium">Avg Response Time</span>
            <span className="text-[13px] font-bold text-[#F59E0B]">{avgResp}</span>
          </div>
        )}
      </div>
    );
  }

  // ── Agents ──
  if (type === 'agents') {
    const agents = Array.isArray(a) ? a : (a.agents || []);
    return (
      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E2E8F0] bg-[#F8F9FB]">
          <UserCog className="h-3.5 w-3.5 text-violet-500" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-violet-500">Analytics</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">Agents</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">{periodLabel[period] || period}</span>
        </div>
        {agents.length > 0 ? (
          <div className="divide-y divide-[#F1F5F9]">
            <div className="grid grid-cols-3 px-3 py-1.5 bg-[#F8F9FB]">
              <span className="text-[9.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">Agent</span>
              <span className="text-[9.5px] font-semibold uppercase tracking-wider text-[#94A3B8] text-center">Chats</span>
              <span className="text-[9.5px] font-semibold uppercase tracking-wider text-[#94A3B8] text-right">Messages</span>
            </div>
            {agents.slice(0, 6).map((ag: any, i: number) => (
              <div key={i} className="grid grid-cols-3 items-center px-3 py-2.5">
                <div>
                  <p className="text-[11.5px] font-semibold text-[#0F172A] truncate">{ag.name || ag.agent_name || `Agent ${i + 1}`}</p>
                  {ag.isOnline !== undefined && (
                    <span className={cn('text-[9.5px] font-medium', ag.isOnline ? 'text-emerald-500' : 'text-[#94A3B8]')}>{ag.isOnline ? 'Online' : 'Offline'}</span>
                  )}
                </div>
                <p className="text-[12px] font-bold text-[#8B5CF6] text-center">{fmtNum(ag.totalConversations ?? ag.conversations ?? 0)}</p>
                <p className="text-[12px] font-bold text-[#22C55E] text-right">{fmtNum(ag.totalMessages ?? ag.messagesSent ?? ag.messages_sent ?? 0)}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-5 text-center text-[12px] text-[#94A3B8]">No agent data available.</div>
        )}
      </div>
    );
  }

  // ── Campaigns ──
  if (type === 'campaigns') {
    const campaigns = Array.isArray(a) ? a : (a.campaigns || []);
    return (
      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E2E8F0] bg-[#F8F9FB]">
          <Megaphone className="h-3.5 w-3.5 text-violet-500" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-violet-500">Analytics</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">Campaigns</span>
          <span className="text-[11px] text-[#94A3B8]">·</span>
          <span className="text-[11px] font-medium text-[#64748B]">{periodLabel[period] || period}</span>
        </div>
        {campaigns.length > 0 ? (
          <div className="divide-y divide-[#F1F5F9]">
            <div className="grid grid-cols-4 px-3 py-1.5 bg-[#F8F9FB]">
              <span className="col-span-2 text-[9.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">Campaign</span>
              <span className="text-[9.5px] font-semibold uppercase tracking-wider text-[#94A3B8] text-center">Del.</span>
              <span className="text-[9.5px] font-semibold uppercase tracking-wider text-[#94A3B8] text-right">Read</span>
            </div>
            {campaigns.slice(0, 5).map((c: any, i: number) => {
              const statusMap: Record<string, string> = { completed: '#10B981', running: '#F59E0B', failed: '#EF4444', draft: '#94A3B8' };
              const st = (c.status || '').toLowerCase();
              return (
                <div key={i} className="grid grid-cols-4 items-center px-3 py-2.5 gap-1">
                  <div className="col-span-2">
                    <p className="text-[11px] font-semibold text-[#0F172A] truncate">{c.name || c.campaign_name || `Campaign ${i + 1}`}</p>
                    {c.status && <span className="text-[9.5px] font-medium capitalize" style={{ color: statusMap[st] || '#64748B' }}>{c.status}</span>}
                  </div>
                  <p className="text-[11.5px] font-bold text-[#10B981] text-center">{pct(c.deliveryRate ?? c.delivery_rate)}</p>
                  <p className="text-[11.5px] font-bold text-[#8B5CF6] text-right">{pct(c.readRate ?? c.read_rate)}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-5 text-center text-[12px] text-[#94A3B8]">No campaign data available.</div>
        )}
      </div>
    );
  }

  // ── Fallback ──
  return (
    <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E2E8F0] bg-[#F8F9FB]">
        <BarChart3 className="h-3.5 w-3.5 text-violet-500" />
        <span className="text-[11px] font-semibold uppercase tracking-widest text-violet-500">Analytics</span>
        <span className="text-[11px] text-[#94A3B8]">·</span>
        <span className="text-[11px] font-medium text-[#64748B]">{periodLabel[period] || period}</span>
      </div>
      <div className="px-4 py-5 text-center text-[12px] text-[#94A3B8]">No analytics data for this period.</div>
    </div>
  );
}

// ── Campaign analytics card ───────────────────────────────────────────────────
function CampaignAnalyticsCard({ data }: { data: any }) {
  // Backend returns flat fields: sent_count, delivered_count, read_count, failed_count
  const sent = data.sent_count ?? data.analytics?.sent ?? 0;
  const delivered = data.delivered_count ?? data.analytics?.delivered ?? 0;
  const read = data.read_count ?? data.analytics?.read ?? 0;
  const failed = data.failed_count ?? data.analytics?.failed ?? 0;
  const total = sent || 1;
  const metrics = [
    { label: 'Sent', value: sent, color: 'bg-blue-400', pct: Math.round((sent / total) * 100) },
    { label: 'Delivered', value: delivered, color: 'bg-emerald-400', pct: Math.round((delivered / total) * 100) },
    { label: 'Read', value: read, color: 'bg-violet-400', pct: Math.round((read / total) * 100) },
    { label: 'Failed', value: failed, color: 'bg-red-400', pct: Math.round((failed / total) * 100) },
  ];
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label={data.name || 'Campaign Analytics'} count={`${sent.toLocaleString()} sent`} />
      <div className="divide-y divide-slate-100/80">
        {metrics.map((m, i) => (
          <div key={i} className="flex items-center gap-3 px-3.5 py-2.5">
            <span className="w-14 shrink-0 text-[11.5px] text-slate-500">{m.label}</span>
            <div className="flex-1 overflow-hidden rounded-full bg-slate-100" style={{ height: 6 }}>
              <div className={cn('h-full rounded-full transition-all', m.color)} style={{ width: `${m.pct || 0}%` }} />
            </div>
            <span className="w-10 shrink-0 text-right text-[11.5px] font-semibold text-slate-700">
              {(m.value || 0).toLocaleString()}
            </span>
            <span className="w-8 shrink-0 text-right text-[10px] text-slate-400">{m.pct || 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Subscription card ─────────────────────────────────────────────────────────
function SubscriptionCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const plan = data.plan || {};
  const usage = data.usage || {};
  const usageEntries = Object.entries(usage).filter(([, v]: any) => v && typeof v === 'object' && 'used' in v);
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <div className="flex items-center justify-between bg-gradient-to-r from-violet-50 to-violet-50/40 px-3.5 py-2.5 border-b border-violet-100">
        <div>
          <p className="text-[13px] font-bold text-slate-800">{plan.name || 'Current Plan'}</p>
          {plan.expires_at && (
            <p className="text-[10.5px] text-slate-400">Renews {new Date(plan.expires_at).toLocaleDateString()}</p>
          )}
        </div>
        <span className={cn('rounded-full px-2.5 py-1 text-[10.5px] font-semibold capitalize',
          plan.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
          {plan.status || 'Active'}
        </span>
      </div>
      {usageEntries.length > 0 && (
        <div className="divide-y divide-slate-100/80 px-3.5 py-1">
          {usageEntries.map(([key, val]: any, i) => {
            const pct = val.limit ? Math.round((val.used / val.limit) * 100) : 0;
            return (
              <div key={i} className="py-2.5">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[11.5px] capitalize text-slate-600">{key.replace(/_/g, ' ')}</span>
                  <span className="text-[11px] text-slate-400">
                    {val.used?.toLocaleString()} {val.limit ? `/ ${val.limit.toLocaleString()}` : ''}
                  </span>
                </div>
                {val.limit && (
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className={cn('h-full rounded-full transition-all', pct > 85 ? 'bg-red-400' : pct > 60 ? 'bg-amber-400' : 'bg-violet-400')}
                      style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="px-3.5 py-2.5 border-t border-slate-100">
        <button type="button" onClick={() => navigate('/home/billing')}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-violet-700 py-2 text-[12px] font-semibold text-white shadow-sm hover:opacity-90">
          Manage Subscription <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Automation flows card ─────────────────────────────────────────────────────
function AutomationFlowsCard({ data, navigate }: { data: any; navigate: NavFn }) {
  const [expanded, setExpanded] = React.useState(false);
  const flows: any[] = data.flows || [];
  const INITIAL = 5;
  const visible = expanded ? flows : flows.slice(0, INITIAL);
  const hiddenCount = flows.length - INITIAL;
  return (
    <div className="overflow-hidden rounded-xl border border-violet-100 bg-white shadow-[0_2px_10px_rgba(109,40,217,0.08)]">
      <CardHeader label="Automation Flows" count={`${data.count} found`} />
      <div className="divide-y divide-slate-100/80">
        {visible.map((f, i) => {
          const isActive = f.status === 'active';
          return (
            <div key={i} className="group flex items-center gap-2.5 px-3.5 py-2.5 transition-colors hover:bg-violet-50/50">
              <span className={cn('mt-0.5 h-2 w-2 shrink-0 rounded-full', isActive ? 'bg-emerald-500' : 'bg-slate-300')} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-medium text-slate-700">{f.name}</p>
                {f.trigger && <p className="text-[11px] text-slate-400">Trigger: {f.trigger}</p>}
              </div>
              <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize group-hover:hidden',
                isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500')}>
                {f.status}
              </span>
              <button type="button"
                onClick={() => navigate(f.id ? `/home/automation/flows/${f.id}` : '/home/automation/flows')}
                className="hidden shrink-0 rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-200 group-hover:block">
                Edit →
              </button>
            </div>
          );
        })}
        {!expanded && hiddenCount > 0 && (
          <button type="button" onClick={() => setExpanded(true)}
            className="w-full px-3.5 py-2 text-center text-[11px] font-medium text-violet-500 transition-colors hover:bg-violet-50">
            + {hiddenCount} more flow{hiddenCount > 1 ? 's' : ''} ↓
          </button>
        )}
      </div>
    </div>
  );
}

// ── Detect if content is rich (has headings, multiple list items, etc.) ─────────
function isRichContent(content: string): boolean {
  if (!content) return false;
  const hasHeading = /^#{1,3}\s/m.test(content);
  const listCount = (content.match(/^[-*\u2022]\s/mg) || []).length + (content.match(/^\d+[.)]/mg) || []).length;
  const hasDivider = /^---+$/m.test(content);
  return hasHeading || listCount >= 3 || hasDivider;
}

// ── Render markdown content ───────────────────────────────────────────────────
function renderContent(content: string): React.ReactNode {
  if (!content) return null;
  const rich = isRichContent(content);
  if (!rich) return renderSimpleContent(content);
  return renderRichContent(content);
}

/** Simple single-paragraph or short-list render (no heading / few items) */
function renderSimpleContent(content: string): React.ReactNode {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: Array<{ type: 'ul' | 'ol'; text: string; emoji?: string }> = [];
  let listKey = 0;

  const flushList = () => {
    if (!listItems.length) return;
    const type = listItems[0].type;
    const key = `list-${listKey++}`;
    if (type === 'ul') {
      elements.push(
        <ul key={key} className="my-2 space-y-1.5">
          {listItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-[7px] h-[7px] w-[7px] shrink-0 rounded-full bg-gradient-to-br from-violet-400 to-emerald-400" />
              <span className="flex-1 leading-relaxed text-slate-700">{parseInline(item.text)}</span>
            </li>
          ))}
        </ul>
      );
    } else {
      elements.push(
        <ol key={key} className="my-2 space-y-1.5">
          {listItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[10px] font-bold text-violet-600">{i + 1}</span>
              <span className="flex-1 leading-relaxed text-slate-700">{parseInline(item.text)}</span>
            </li>
          ))}
        </ol>
      );
    }
    listItems = [];
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      if (elements.length > 0) elements.push(<div key={`gap-${idx}`} className="h-1" />);
      return;
    }
    const ulMatch = trimmed.match(/^[-*•]\s+(.+)/);
    if (ulMatch) { listItems.push({ type: 'ul', text: ulMatch[1] }); return; }
    const olMatch = trimmed.match(/^(\d+)[.)]\s+(.+)/);
    if (olMatch) { listItems.push({ type: 'ol', text: olMatch[2] }); return; }
    flushList();
    elements.push(<p key={idx} className="leading-relaxed text-slate-700">{parseInline(trimmed)}</p>);
  });

  flushList();
  return <div className="space-y-1 px-4 py-3">{elements}</div>;
}

/** Rich render — heading sections, callout boxes, stat rows, multi-list */
function renderRichContent(content: string): React.ReactNode {
  // Split into logical blocks separated by blank lines or headings
  const lines = content.split('\n');
  const blocks: Array<{ type: string; lines: string[] }> = [];
  let current: { type: string; lines: string[] } | null = null;

  const pushCurrent = () => {
    if (current && current.lines.length) { blocks.push(current); current = null; }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    // Heading — match most specific (h3) first to avoid false positives
    const h3 = trimmed.match(/^###\s+(.+)/);
    const h2 = !h3 && trimmed.match(/^##\s+(.+)/);
    const h1 = !h3 && !h2 && trimmed.match(/^#\s+(.+)/);
    if (h1 || h2 || h3) {
      pushCurrent();
      const lvl = h3 ? 'h3' : h2 ? 'h2' : 'h1';
      blocks.push({ type: lvl, lines: [(h3 || h2 || h1)![1]] });
      return;
    }
    // Divider
    if (/^---+$/.test(trimmed)) {
      pushCurrent();
      blocks.push({ type: 'divider', lines: [] });
      return;
    }
    // Bullet
    const ul = trimmed.match(/^[-*•]\s+(.+)/);
    if (ul) {
      if (current?.type !== 'ul') { pushCurrent(); current = { type: 'ul', lines: [] }; }
      current!.lines.push(ul[1]);
      return;
    }
    // Numbered
    const ol = trimmed.match(/^(\d+)[.)]\s+(.+)/);
    if (ol) {
      if (current?.type !== 'ol') { pushCurrent(); current = { type: 'ol', lines: [] }; }
      current!.lines.push(ol[2]);
      return;
    }
    // Empty line
    if (!trimmed) {
      pushCurrent();
      return;
    }
    // Regular paragraph
    if (current?.type !== 'p') { pushCurrent(); current = { type: 'p', lines: [] }; }
    current!.lines.push(trimmed);
  });
  pushCurrent();

  const rendered = blocks.map((block, bi) => {
    switch (block.type) {
      case 'h1':
        return (
          <div key={bi} className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
            <h2 className="text-[15px] font-bold tracking-tight text-slate-900">{parseInline(block.lines[0])}</h2>
          </div>
        );
      case 'h2':
        return (
          <div key={bi} className="flex items-center gap-2 border-b border-slate-100/80 bg-gradient-to-r from-violet-50/70 to-white px-4 py-2.5">
            <span className="h-3 w-1 rounded-full bg-gradient-to-b from-violet-500 to-emerald-400" />
            <h3 className="text-[13px] font-bold tracking-wide text-violet-700 uppercase">{parseInline(block.lines[0])}</h3>
          </div>
        );
      case 'h3':
        return (
          <div key={bi} className="px-4 pt-3 pb-1">
            <h4 className="text-[12.5px] font-semibold text-slate-600 uppercase tracking-wider">{parseInline(block.lines[0])}</h4>
          </div>
        );
      case 'divider':
        return <div key={bi} className="mx-4 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />;
      case 'ul':
        return (
          <div key={bi} className="px-4 py-1.5">
            <ul className="space-y-1.5">
              {block.lines.map((item, i) => (
                <li key={i} className="group flex items-start gap-2.5 rounded-lg px-2 py-1 transition-colors hover:bg-violet-50/60">
                  <span className="mt-[6px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-100 to-emerald-50 ring-1 ring-violet-200/60">
                    <span className="h-[5px] w-[5px] rounded-full bg-gradient-to-br from-violet-500 to-emerald-500" />
                  </span>
                  <span className="flex-1 text-[13px] leading-relaxed text-slate-700">{parseInline(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      case 'ol':
        return (
          <div key={bi} className="px-4 py-1.5">
            <ol className="space-y-1.5">
              {block.lines.map((item, i) => (
                <li key={i} className="group flex items-start gap-2.5 rounded-lg px-2 py-1 transition-colors hover:bg-violet-50/60">
                  <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-emerald-500 text-[10px] font-bold text-white shadow-sm">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-[13px] leading-relaxed text-slate-700">{parseInline(item)}</span>
                </li>
              ))}
            </ol>
          </div>
        );
      case 'p': {
        // Check if it looks like a key:value stat line e.g. "Messages: 42"
        const statLines = block.lines.filter(l => /^.+:\s*[\d.,]+/.test(l));
        if (statLines.length >= 2 && statLines.length === block.lines.length) {
          // Render as a compact stat grid
          return (
            <div key={bi} className="mx-3 my-2 grid grid-cols-2 gap-1.5">
              {block.lines.map((line, i) => {
                const [label, ...rest] = line.split(':');
                const val = rest.join(':').trim();
                return (
                  <div key={i} className="flex flex-col rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50/80 px-3 py-2.5 shadow-sm">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label.trim()}</span>
                    <span className="mt-0.5 text-[15px] font-bold text-slate-800">{val}</span>
                  </div>
                );
              })}
            </div>
          );
        }
        return (
          <div key={bi} className="px-4 py-1">
            {block.lines.map((line, i) => (
              <p key={i} className="leading-relaxed text-[13px] text-slate-700">{parseInline(line)}</p>
            ))}
          </div>
        );
      }
      default:
        return null;
    }
  });

  return <div className="space-y-0 py-1">{rendered}</div>;
}

// ── Send message card ─────────────────────────────────────────────────────────
function SendMessageCard({ data }: { data: any }) {
  return (
    <div className="overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-[0_2px_10px_rgba(16,185,129,0.08)]">
      <div className="flex items-center gap-2 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-emerald-50/40 px-3.5 py-2">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <span className="text-[10.5px] font-bold uppercase tracking-widest text-emerald-600">Message Sent</span>
      </div>
      <div className="px-3.5 py-2.5 text-[12.5px] text-slate-600">
        {data.message || 'Message delivered successfully.'}
      </div>
    </div>
  );
}

function parseInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4)
      return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2)
      return <em key={i} className="text-slate-600">{part.slice(1, -1)}</em>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} className="rounded-md border border-violet-100 bg-violet-50 px-1.5 py-0.5 font-mono text-[11.5px] text-violet-700">{part.slice(1, -1)}</code>;
    return part;
  });
}
